// src/App.jsx
// Landmark Legacy Room — M&A deal management for Landmark Funeral Group

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Building2, X, Plus, Briefcase, Users, UserPlus, Settings, LogOut, Loader2, Trash2, Calendar, MapPin, FileText, ChevronDown, ChevronLeft, ChevronRight, Download, FileDown, Activity, DollarSign, Moon, Sun, Sparkles, Landmark, Upload, Pencil, Target, Search, GripVertical, Rocket, Bell
} from 'lucide-react';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth, useDeals, useTeam, useDealStages, useLfgLocations, useTerritories, useDealReasonCategories } from './hooks/useSupabase';
import { supabase, validateSellerToken, sanitizeDealName } from './lib/supabase';
import { titleCaseIfShouting } from './lib/textFormat';
import { ThemeProvider, useTheme, useDisplayColors } from './lib/ThemeContext';
import BriefingsPanel from './components/BriefingsPanel';

// Microsoft 4-square logo, inline so it never breaks if a CDN/host blocks hot-linking.
function MsLogo({ className = 'w-5 h-5' }) {
  return (
    <svg viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      <rect x="1"  y="1"  width="9" height="9" fill="#F25022" />
      <rect x="11" y="1"  width="9" height="9" fill="#7FBA00" />
      <rect x="1"  y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
import { MfaEnrollScreen, MfaChallengeScreen } from './components/MfaScreens';
import DealView from './components/DealView';
import AdminView from './components/AdminView';
import SellerPortal from './components/SellerPortal';
import PublicSellerPortal from './components/PublicSellerPortal';
import TimelineView from './components/TimelineView';
import MapView from './components/MapView';
import FilesView from './components/FilesView';
import ActivityFeedView from './components/ActivityFeedView';
import VendorsView from './components/VendorsView';
import FinanceView from './components/FinanceView';
import MarketShareView from './components/MarketShareView';
import OpportunityView from './components/OpportunityView';
import OrgChartView from './components/OrgChartView';
import ProspectDiscoveryTab from './components/ProspectDiscoveryTab';
import BuyerEntitiesView from './components/BuyerEntitiesView';
import TerritoryMapEditor, { pointInPolygon } from './components/TerritoryMapEditor';
import { US_COUNTY_CENTROIDS } from './data/usCountyCentroids';
// LFG Brand Colors
const LFG_COLORS = {
  navy: '#1A3665',      // Primary - Deep navy blue
  gray: '#707578',      // Secondary - Medium gray
  green: '#85BD66',     // Accent - Light green
};

// Google Drive logo URL (extracted from shared link)
// File ID: 1eHk-3IzJJ4E8r17F6f222CdSbMrn9wat
const LFG_LOGO_URL = 'https://hyqjtpsiwbumowpljwyc.supabase.co/storage/v1/object/public/public-assets/LFGLogo.png';

// Sortable wrapper for deal rows. Applies @dnd-kit transform/transition and
// exposes `listeners` + `attributes` for the grip handle inside each row.
// Must be defined outside the App component so React doesn't recreate it on
// every render (which would unmount/remount every row while dragging).
//
// Only set inline styles when actually transforming/settling — an idle wrapper
// with opacity:1 / position:relative inline triggers the dark-mode
// MutationObserver on every React re-render, causing color flicker.
function SortableDealItem({ id, disabled, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const inlineStyle = {};
  if (transform) inlineStyle.transform = CSS.Transform.toString(transform);
  if (transition) inlineStyle.transition = transition;
  if (isDragging) { inlineStyle.opacity = 0.4; inlineStyle.zIndex = 10; }
  return (
    <div
      ref={setNodeRef}
      className="relative"
      style={Object.keys(inlineStyle).length ? inlineStyle : undefined}
    >
      {children({ listeners, attributes })}
    </div>
  );
}

// Deal phases configuration (fallback if database not loaded)
// Fallback list when useDealStages hasn't resolved yet (or fails). Kept in
// sync with deal_stages in the DB. Chronological cycle + On Hold (off-cycle,
// non-terminal) + the two terminal states.
const DEFAULT_PHASES = [
  { id: 'future-target', name: 'Future Target', color: '#94A3B8' },
  { id: 'prospect',      name: 'Prospect',      color: '#6B7280' },
  { id: 'diligence',     name: 'Diligence',     color: '#EAB308' },
  { id: 'offer-made',    name: 'Offer Made',    color: '#F97316' },
  { id: 'under-loi',     name: 'Under LOI',     color: '#3B82F6' },
  { id: 'on-hold',       name: 'On Hold',       color: '#A855F7' },
  { id: 'closed',        name: 'Closed',        color: '#22C55E', is_terminal: true },
  { id: 'canceled',      name: 'Canceled',      color: '#EF4444', is_terminal: true },
];

function AppInner() {
  const { user, userProfile, organization, loading: authLoading, error: authError, signIn, signUp, signOut, resetPassword, signInWithMicrosoft, isAdmin, isViewer, isLimitedViewer, isDealAdvisor, isIntegration, isBoardMember, mfaState, mfaFactors, refreshMfaState, bootstrapSignUp, bootstrapOrganization, orgConfigLoading, orgExists, ssoEnabled, passwordAuthEnabled, publicOrgBranding } = useAuth();
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(organization?.primary_color, organization?.secondary_color);
  // Sidebar user menu needs dark-mode toggle at the app level
  const { darkMode, setDarkMode } = useTheme();
  
  // Check if this is a seller portal URL
  const [sellerToken, setSellerToken] = useState(null);
  const [isPublicSellerPortal, setIsPublicSellerPortal] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [signingUp, setSigningUp] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState('');
  
  // Read view/dealId from the URL hash, e.g. #/portfolio, #/deal/<uuid>, #/admin
  const readStateFromUrl = () => {
    const m = (window.location.hash || '').match(/^#\/([\w-]+)(?:\/(.+))?$/);
    if (!m) return null;
    const v = m[1];
    return { view: v, activeDealId: v === 'deal' ? m[2] : null };
  };

  // View state — URL hash takes precedence (so shared URLs work + back button
  // restores prior state), then sessionStorage (so plain refresh preserves
  // current location even without a hash), then default 'portfolio'.
  const [view, setView] = useState(() => {
    const fromUrl = readStateFromUrl();
    if (fromUrl) return fromUrl.view;
    try {
      const savedView = sessionStorage.getItem('lfg_view');
      const savedDealId = sessionStorage.getItem('lfg_activeDealId');
      if (savedView === 'deal' && !savedDealId) return 'portfolio';
      return savedView || 'portfolio';
    } catch { return 'portfolio'; }
  });
  const [activeDealId, setActiveDealId] = useState(() => {
    const fromUrl = readStateFromUrl();
    if (fromUrl?.activeDealId) return fromUrl.activeDealId;
    try { return sessionStorage.getItem('lfg_activeDealId') || null; } catch { return null; }
  });

  // Track whether the last view/dealId change was caused by a popstate event
  // (browser back/forward). When it was, we shouldn't push a NEW history entry
  // — that would break the back button by stacking duplicates.
  const fromPopstateRef = useRef(false);
  const [sellerDealId, setSellerDealId] = useState(null);
  const [adminTab, setAdminTab] = useState('branding');

  // Persist view state to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem('lfg_view', view);
      if (activeDealId) {
        sessionStorage.setItem('lfg_activeDealId', activeDealId);
      } else {
        sessionStorage.removeItem('lfg_activeDealId');
      }
    } catch (e) {
      console.warn('Could not save to sessionStorage:', e);
    }
  }, [view, activeDealId]);

  // Sync view → browser history. Browser back/forward then navigates between
  // in-app screens (portfolio ↔ deal ↔ admin etc) instead of jumping all the
  // way back to the login page.
  useEffect(() => {
    if (fromPopstateRef.current) {
      // This change was triggered by the user clicking back/forward — DON'T
      // push a duplicate entry, just clear the flag.
      fromPopstateRef.current = false;
      return;
    }
    // CRITICAL: don't touch the hash when it contains an OAuth/auth callback.
    // After Microsoft SSO redirect, the URL is e.g.
    // `/#access_token=...&refresh_token=...&type=...` and the Supabase JS
    // client reads those tokens from the hash to complete sign-in. If we
    // overwrite the hash before Supabase processes it, sign-in silently
    // fails and the user bounces back to the login screen forever.
    const currentHash = window.location.hash || '';
    if (/(?:^|[#&])(access_token|refresh_token|provider_token|expires_in|expires_at|token_type|type=recovery|type=signup|error_code|error_description)=/.test(currentHash)) {
      return;
    }
    const hash = view === 'deal' && activeDealId
      ? `#/deal/${activeDealId}`
      : `#/${view}`;
    if (currentHash === hash) return;
    // First time we touch history (initial mount): replaceState so we don't
    // stack a phantom entry on top of whatever loaded first. After that:
    // pushState on every screen change so back has somewhere to go.
    const isFirst = !window.history.state || window.history.state.lfg !== true;
    const stateObj = { lfg: true, view, activeDealId };
    if (isFirst) {
      window.history.replaceState(stateObj, '', hash);
    } else {
      window.history.pushState(stateObj, '', hash);
    }
  }, [view, activeDealId]);

  // After auth completes, Supabase clears the OAuth hash, leaving the URL
  // bare. At that point we want our `#/portfolio`-style hash to take over.
  // Watching `user` triggers our hash sync once auth is settled.
  useEffect(() => {
    if (!user) return;
    const currentHash = window.location.hash || '';
    if (/(?:^|[#&])(access_token|refresh_token|provider_token|expires_in|expires_at|token_type|type=recovery|type=signup|error_code|error_description)=/.test(currentHash)) {
      return;
    }
    const hash = view === 'deal' && activeDealId
      ? `#/deal/${activeDealId}`
      : `#/${view}`;
    if (currentHash === hash) return;
    window.history.replaceState({ lfg: true, view, activeDealId }, '', hash);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [user]);

  // Listen for browser back/forward and sync view state from history entry
  useEffect(() => {
    const handler = (e) => {
      fromPopstateRef.current = true;
      const s = e.state;
      if (s && s.lfg) {
        setView(s.view || 'portfolio');
        setActiveDealId(s.activeDealId || null);
      } else {
        const fromUrl = readStateFromUrl();
        if (fromUrl) {
          setView(fromUrl.view);
          setActiveDealId(fromUrl.activeDealId);
        } else {
          setView('portfolio');
          setActiveDealId(null);
        }
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // Clear deal view if activeDealId becomes null
  useEffect(() => {
    if (view === 'deal' && !activeDealId) {
      setView('portfolio');
    }
  }, [view, activeDealId]);
  
  // UI state
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState([]);
  const [showModal, setShowModal] = useState(null);
  const [uploading, setUploading] = useState({});
  const [toast, setToast] = useState(null);

  // Magic-link / recovery invites route through our own interstitial
  // "click to sign in" page rather than emailing Supabase's raw one-click
  // verify link directly. Corporate email security (Outlook Safe Links,
  // Mimecast, etc.) pre-fetches links in incoming mail to scan them —
  // that GET request silently consumes a magic link's single-use token
  // before the real recipient ever clicks it, dumping them at the login
  // screen with no explanation. Reading this once on mount (not on every
  // render) avoids re-parsing the URL after we strip it post-verification.
  const [pendingAuthLink, setPendingAuthLink] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('link_token');
    const type = params.get('link_type');
    return (token && type)
      ? { token, type, email: params.get('link_email') || '', dealId: params.get('deal') || null }
      : null;
  });

  // Form state
  const [newDealName, setNewDealName] = useState('');
  const [newMember, setNewMember] = useState({ name: '', email: '', role: 'member' });
  const [newTask, setNewTask] = useState({ phase: '', name: '', is_doc_request: false, relative_days: 0, base_date: 'loi' });
  const [requestMsg, setRequestMsg] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '', confirmPassword: '', orgName: '', fullName: '', isSignUp: false });

  // Check URL for seller portal token on mount.
  // Matches both /seller/<token> and /seller-portal/<token>.
  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/^\/seller(?:-portal)?\/([a-f0-9]+)$/i);
    if (match) {
      setSellerToken(match[1]);
      setIsPublicSellerPortal(true);
    }
  }, []);

  // Check for password reset flow (Supabase adds hash params)
  useEffect(() => {
    const hash = window.location.hash;
    const searchParams = new URLSearchParams(window.location.search);
    
    console.log('Checking for password reset - hash:', hash);
    console.log('Checking for password reset - search:', window.location.search);
    console.log('Recovery mode flag:', window.__passwordRecoveryMode);
    
    // Check if useAuth detected recovery mode
    if (window.__passwordRecoveryMode) {
      console.log('Password recovery mode detected via flag');
      setShowPasswordReset(true);
      window.__passwordRecoveryMode = false; // Clear flag
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    
    // Check for errors in hash (expired link, etc.)
    if (hash) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const error = hashParams.get('error');
      const errorDescription = hashParams.get('error_description');
      
      if (error) {
        console.log('Auth error:', error, errorDescription);
        // Show error to user
        setTimeout(() => {
          showToast(errorDescription?.replace(/\+/g, ' ') || 'Link expired or invalid. Please request a new one.', 'error');
        }, 500);
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      
      // Check hash for recovery token (Supabase v2 format)
      const type = hashParams.get('type');
      const accessToken = hashParams.get('access_token');
      
      console.log('Hash params - type:', type, 'has token:', !!accessToken);
      
      if (type === 'recovery' || (accessToken && hash.includes('recovery'))) {
        console.log('Password reset detected via hash');
        setShowPasswordReset(true);
        return;
      }
    }
    
    // Check query params (alternative format)
    if (searchParams.get('type') === 'recovery') {
      console.log('Password reset detected via query params');
      setShowPasswordReset(true);
    }
    
    // Clean up reset=true from URL if present
    if (searchParams.get('reset') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [user]); // Re-run when user changes (after Supabase processes token)

  // Branding colors - use organization settings or fall back to LFG defaults
  const pc = organization?.primary_color || LFG_COLORS.navy;
  const sc = organization?.secondary_color || LFG_COLORS.gray;
  const accent = LFG_COLORS.green;

  // Toast helper
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // If this is a public seller portal URL, show that instead
  if (isPublicSellerPortal && sellerToken) {
    return <PublicSellerPortal token={sellerToken} orgConfig={organization} />;
  }

  // Auth loading state — wait for both the session check and the org config fetch
  if (authLoading || orgConfigLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: displayNavy }} />
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    );
  }

  // First-run: no organization exists yet for this deployment — show the
  // self-service setup wizard instead of a normal login screen. This
  // covers both "nobody has signed up at all yet" (user is null) and
  // "just finished a bootstrap sign-up/SSO redirect, but org creation
  // hasn't happened yet" (user is set, profile isn't) — gated strictly on
  // !orgExists, never merely on a missing profile, so a legitimately
  // locked-out external user with a stale invite doesn't get offered
  // "create the organization" by mistake.
  if (!orgExists) {
    return (
      <BootstrapWizard
        user={user}
        ssoEnabled={ssoEnabled}
        signInWithMicrosoft={signInWithMicrosoft}
        bootstrapSignUp={bootstrapSignUp}
        bootstrapOrganization={bootstrapOrganization}
        signOut={signOut}
        showToast={showToast}
      />
    );
  }

  // Interstitial "click to sign in" gate for magic-link / recovery
  // invite links — must render before AuthForm, and before any session
  // exists, so a scanner-safe link isn't swallowed by the generic login
  // screen. See pendingAuthLink's declaration above for why this exists.
  if (pendingAuthLink) {
    return (
      <MagicLinkInterstitial
        tokenHash={pendingAuthLink.token}
        tokenType={pendingAuthLink.type}
        email={pendingAuthLink.email}
        resetPassword={resetPassword}
        onConsumed={() => {
          window.history.replaceState({}, '', window.location.pathname);
          // A 'recovery' token means the whole point was to set a new
          // password (invite's initial "Set Password" link, or a password
          // reset) — verifyOtp alone just signs them in with whatever
          // password already existed (a random one, for a first-time
          // invite), so route into the actual set-password screen instead
          // of dropping them straight into the dashboard.
          if (pendingAuthLink.type === 'recovery') {
            setShowPasswordReset(true);
          } else if (pendingAuthLink.dealId) {
            setActiveDealId(pendingAuthLink.dealId);
            setView('deal');
          }
          setPendingAuthLink(null);
        }}
      />
    );
  }

  // Login/Signup form - also show if signingUp to prevent dashboard flash
  if (!user || signingUp) {
    return <AuthForm loginForm={loginForm} setLoginForm={setLoginForm} signIn={signIn} signUp={signUp} resetPassword={resetPassword} signInWithMicrosoft={signInWithMicrosoft} showToast={showToast} authError={authError} setSigningUp={setSigningUp} signupSuccess={signupSuccess} setSignupSuccess={setSignupSuccess} ssoEnabled={ssoEnabled} passwordAuthEnabled={passwordAuthEnabled} />;
  }

  // Password reset form (user clicked reset link in email)
  if (showPasswordReset) {
    return <PasswordResetForm onComplete={() => setShowPasswordReset(false)} showToast={showToast} />;
  }

  // MFA gate — required for all email/password users. SSO users skip
  // automatically (their IdP handles MFA). Cannot reach the app without
  // either enrolling a TOTP factor or verifying one for this session.
  if (mfaState === 'enrollment_required') {
    return <MfaEnrollScreen user={user} onComplete={refreshMfaState} onSignOut={signOut} />;
  }
  if (mfaState === 'challenge_required') {
    return <MfaChallengeScreen user={user} factorId={mfaFactors[0]?.id} onComplete={refreshMfaState} onSignOut={signOut} />;
  }

  // Render based on view
  if (view === 'admin') {
    // shellProps isn't defined until later in the function; build inline.
    const adminShellProps = {
      organization, userProfile, pc,
      setView, setDarkMode, darkMode,
      signOut, isAdmin, isViewer, isLimitedViewer, isDealAdvisor, isIntegration, isBoardMember,
      toast,
    };
    return (
      <AppShell {...adminShellProps} currentView="admin">
        <AdminView
          organization={organization}
          pc={pc}
          sc={sc}
          adminTab={adminTab}
          setAdminTab={setAdminTab}
          setView={setView}
          newTask={newTask}
          setNewTask={setNewTask}
          showToast={showToast}
          signOut={signOut}
          isAdmin={isAdmin}
          readOnly={isBoardMember && !isAdmin}
        />
      </AppShell>
    );
  }

  if (view === 'seller' && sellerDealId) {
    return (
      <SellerPortal
        dealId={sellerDealId}
        organization={organization}
        pc={pc}
        setView={setView}
        setSellerDealId={setSellerDealId}
        showToast={showToast}
      />
    );
  }

  // Shared props for AppShell — identical across all peer views (incl. DealView)
  const shellProps = {
    organization, userProfile, pc,
    setView, setDarkMode, darkMode,
    signOut, isAdmin, isViewer, isLimitedViewer, isDealAdvisor, isIntegration, isBoardMember,
    toast,
  };

  if (view === 'deal' && activeDealId) {
    // Render inside AppShell so the sidebar (with user menu, dark-mode toggle,
    // sign-out, settings) is visible on deal pages too. AppShell already
    // wraps children in <main>, which the dark-mode filter scopes to.
    return (
      <AppShell {...shellProps} currentView="deal">
        <DealView
          dealId={activeDealId}
          organization={organization}
          userProfile={userProfile}
          pc={pc}
          sc={sc}
          isViewer={isViewer}
          isLimitedViewer={isLimitedViewer}
          isIntegration={isIntegration}
          isBoardMember={isBoardMember}
          isDealAdvisor={isDealAdvisor}
          expanded={expanded}
          setExpanded={setExpanded}
          selected={selected}
          setSelected={setSelected}
          showModal={showModal}
          setShowModal={setShowModal}
          newTask={newTask}
          setNewTask={setNewTask}
          requestMsg={requestMsg}
          setRequestMsg={setRequestMsg}
          setView={setView}
          setSellerDealId={setSellerDealId}
          uploading={uploading}
          setUploading={setUploading}
          showToast={showToast}
          signOut={signOut}
          onBack={() => {
            setActiveDealId(null);
            setView('portfolio');
            setSelected([]);
          }}
        />
      </AppShell>
    );
  }

  if (view === 'timeline') {
    // Timeline shows milestones org-wide with no per-deal scoping —
    // deal_advisor should only ever see their one deal's Diligence tab.
    if (isDealAdvisor) { setView('portfolio'); return null; }
    return (
      <AppShell {...shellProps} currentView="timeline">
        <TimelineViewWrapper
          organization={organization}
          pc={pc} sc={sc}
          setView={setView}
          setActiveDealId={setActiveDealId}
          signOut={signOut}
        />
      </AppShell>
    );
  }

  if (view === 'map') {
    // Map shows every LFG/competitor/deal location org-wide with no
    // per-deal scoping — same reasoning as Timeline above.
    if (isDealAdvisor) { setView('portfolio'); return null; }
    return (
      <AppShell {...shellProps} currentView="map">
        <MapViewWrapper
          organization={organization}
          pc={pc} sc={sc}
          setView={setView}
          setActiveDealId={setActiveDealId}
          signOut={signOut}
        />
      </AppShell>
    );
  }


  if (view === 'activity') {
    return (
      <AppShell {...shellProps} currentView="activity">
        <ActivityFeedView
          organization={organization}
          onBack={() => setView('portfolio')}
          onSelectDeal={(dealId) => { setActiveDealId(dealId); setView('deal'); }}
          pc={pc} sc={sc} signOut={signOut}
          isIntegration={isIntegration}
        />
      </AppShell>
    );
  }

  if (view === 'vendors') {
    return (
      <AppShell {...shellProps} currentView="vendors">
        <VendorsView
          organizationId={organization?.id}
          userProfile={userProfile}
          pc={pc} sc={sc}
          isViewer={isViewer}
          onBack={() => setView('portfolio')}
          showToast={showToast}
          signOut={signOut}
        />
      </AppShell>
    );
  }

  if (view === 'finance') {
    // Integration users can't see financial pages — bounce them back to
    // the portfolio if they navigate here via URL hash or stale state.
    if (isIntegration) {
      setView('portfolio');
      return null;
    }
    return (
      <AppShell {...shellProps} currentView="finance">
        <FinanceView
          organizationId={organization?.id}
          pc={pc} sc={sc}
          onBack={() => setView('portfolio')}
          showToast={showToast}
          signOut={signOut}
          setActiveDealId={setActiveDealId}
          setView={setView}
        />
      </AppShell>
    );
  }

  if (view === 'market') {
    return (
      <AppShell {...shellProps} currentView="market">
        <MarketShareView
          organization={organization}
          pc={pc} sc={sc}
          isAdmin={isAdmin}
          signOut={signOut}
        />
      </AppShell>
    );
  }

  if (view === 'opportunity') {
    if (isIntegration) { setView('portfolio'); return null; }
    return (
      <AppShell {...shellProps} currentView="opportunity">
        <OpportunityView
          organization={organization}
          pc={pc} sc={sc}
        />
      </AppShell>
    );
  }

  if (view === 'orgchart') {
    // Org chart is internal-only; deal_advisor is the one role we hide it
    // from (RLS enforces this server-side too).
    if (isDealAdvisor) { setView('portfolio'); return null; }
    return (
      <AppShell {...shellProps} currentView="orgchart">
        <OrgChartView
          organization={organization}
          isAdmin={isAdmin}
        />
      </AppShell>
    );
  }

  if (view === 'files') {
    // Integration users can't see the global files view — files may
    // contain financial / legal docs they shouldn't have visibility into.
    if (isIntegration) { setView('portfolio'); return null; }
    return (
      <AppShell {...shellProps} currentView="files">
        <FilesView
          organizationId={organization?.id}
          onBack={() => setView('portfolio')}
          onSelectDeal={(dealId) => { setActiveDealId(dealId); setView('deal'); }}
          pc={pc} sc={sc} signOut={signOut}
          showToast={showToast}
        />
      </AppShell>
    );
  }

  if (view === 'discovery') {
    if (isIntegration) { setView('portfolio'); return null; }
    return (
      <AppShell {...shellProps} currentView="discovery">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-12">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-3" style={{
              background: 'linear-gradient(135deg, #0F172A 0%, #1A3665 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.02em',
            }}>
              Prospects
            </h1>
            <p className="text-sm mt-1" style={{ color: '#475569' }}>AI-powered discovery and evaluation of acquisition targets</p>
          </div>
          <ProspectDiscoveryTab
            organizationId={organization?.id}
            primaryColor={pc}
            secondaryColor={sc}
            showToast={showToast}
            onAddToPipeline={(dealId) => { setActiveDealId(dealId); setView('deal'); }}
          />
        </div>
      </AppShell>
    );
  }

  if (view === 'entities') {
    if (isIntegration) { setView('portfolio'); return null; }
    return (
      <AppShell {...shellProps} currentView="entities">
        <BuyerEntitiesView
          organizationId={organization?.id}
          userProfile={userProfile}
          pc={pc} sc={sc}
          isViewer={isViewer}
          onBack={() => setView('portfolio')}
          showToast={showToast}
          signOut={signOut}
        />
      </AppShell>
    );
  }

  // Default: Portfolio view
  return (
    <>
      <PortfolioView
        organization={organization}
        userProfile={userProfile}
        pc={pc}
        sc={sc}
        accent={accent}
        isViewer={isViewer}
        isLimitedViewer={isLimitedViewer}
        isIntegration={isIntegration}
        isBoardMember={isBoardMember}
        isDealAdvisor={isDealAdvisor}
        isAdmin={isAdmin}
        setView={setView}
        setActiveDealId={setActiveDealId}
        showModal={showModal}
        setShowModal={setShowModal}
        newDealName={newDealName}
        setNewDealName={setNewDealName}
        newMember={newMember}
        setNewMember={setNewMember}
        signOut={signOut}
        showToast={showToast}
      />
      {toast && (
        <div className={`fixed bottom-4 right-4 z-[100] px-4 py-3 rounded-lg shadow-lg text-white ${
          toast.type === 'error' ? 'bg-red-500' : ''
        }`} style={{ backgroundColor: toast.type === 'error' ? undefined : LFG_COLORS.green }}>
          {toast.message}
        </div>
      )}
    </>
  );
}

// ============================================================================
// AUTH FORM COMPONENT - M365 SSO Primary + Email/Password Backup
// ============================================================================
function AuthForm({ loginForm, setLoginForm, signIn, signUp, resetPassword, signInWithMicrosoft, showToast, authError, setSigningUp, signupSuccess, setSignupSuccess, ssoEnabled = true, passwordAuthEnabled = true }) {
  // Deliberately NOT org-driven (unlike most of the app) — this screen's
  // branding must render identically for the current deployment no matter
  // what's in the organizations row. Only a genuinely separate deployment
  // (a forked company with its own hardcoded constants swapped in) should
  // ever look different here.
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(null, null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // 'sso', 'login', 'signup', 'reset', 'reset-sent'. Skip the method-picker
  // screen entirely when only one sign-in method is configured for this
  // org — password-only orgs land straight on the login form.
  const [mode, setMode] = useState(() => (!ssoEnabled && passwordAuthEnabled) ? 'login' : 'sso');
  const [inviteData, setInviteData] = useState(null);
  const [showEmailLogin, setShowEmailLogin] = useState(() => !ssoEnabled && passwordAuthEnabled);

  // Returns to the picker screen (or the only available method, if just
  // one is enabled) — used by every "back"/"cancel" action below instead
  // of hardcoding mode='sso'.
  const goToDefaultScreen = () => {
    if (!ssoEnabled && passwordAuthEnabled) { setMode('login'); setShowEmailLogin(true); }
    else { setMode('sso'); setShowEmailLogin(false); }
    setError('');
  };

  // Check URL for invite token or email
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inviteEmail = params.get('invite');
    const inviteName = params.get('name');
    
    if (inviteEmail) {
      setInviteData({ email: inviteEmail, name: inviteName || '' });
      setLoginForm(p => ({ ...p, email: inviteEmail, fullName: inviteName || '' }));
      setMode('signup');
      setShowEmailLogin(true);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [setLoginForm]);

  // Show auth error from hook if present
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  // Check if error is a domain restriction error
  const isDomainError = error && error.includes('landmarkfuneralgroup.com');

  const handleMicrosoftSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      const { error } = await signInWithMicrosoft();
      if (error) throw error;
      // OAuth redirect will handle the rest
    } catch (err) {
      setError(err.message || 'Failed to sign in with Microsoft. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (loading) return;
    
    setLoading(true);
    setError('');

    try {
      if (mode === 'signup') {
        if (!inviteData) {
          throw new Error('Signup is invite-only. Please use the link from your invitation email.');
        }
        if (loginForm.password !== loginForm.confirmPassword) {
          throw new Error('Passwords do not match.');
        }
        if (loginForm.password.length < 8) {
          throw new Error('Password must be at least 8 characters.');
        }
        if (!/[A-Z]/.test(loginForm.password) || !/[a-z]/.test(loginForm.password) || !/[0-9]/.test(loginForm.password)) {
          throw new Error('Password must include uppercase, lowercase, and a number.');
        }
        const result = await signUp(loginForm.email, loginForm.password, loginForm.fullName);
        if (result.error) throw result.error;
        
        // Only set signingUp AFTER success (setting it before unmounts AuthForm and loses error state)
        if (setSigningUp) setSigningUp(true);
        if (setSigningUp) setSigningUp(false);
        
        // Set success message at App level (survives AuthForm unmount/remount)
        if (setSignupSuccess) setSignupSuccess('Account created successfully! Please sign in.');
        setMode('login');
        setInviteData(null);
        // Keep email filled in for convenience
        setLoginForm(prev => ({ ...prev, password: '', confirmPassword: '' }));
      } else if (mode === 'reset') {
        const { error } = await resetPassword(loginForm.email);
        if (error) throw error;
        setMode('reset-sent');
      } else {
        const { error } = await signIn(loginForm.email, loginForm.password);
        if (error) throw error;
      }
    } catch (err) {
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8 w-full max-w-md">
        <div className="text-center mb-8">
          {/* LFG Logo */}
          <div className="flex justify-center mb-4">
            <img
              src={LFG_LOGO_URL}
              alt="LFG Logo"
              className="h-20 w-auto object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden items-center justify-center w-20 h-20 rounded-lg" style={{ backgroundColor: `${LFG_COLORS.navy}15` }}>
              <Briefcase className="w-10 h-10" style={{ color: displayNavy }} />
            </div>
          </div>
          <h1 className="text-2xl font-bold" style={{ color: displayNavy }}>Landmark Legacy Room</h1>
          <p className="text-sm mt-2" style={{ color: displaySecondary }}>
            M&A Due Diligence Management
          </p>
        </div>

        {/* Domain restriction error - special styling */}
        {isDomainError && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="font-medium text-amber-800">Access Restricted</p>
                <p className="text-sm text-amber-700 mt-1">
                  Your account isn't recognized for this portal. If you're a Landmark employee,
                  sign in with your work Microsoft 365 account. Otherwise, use the
                  email-and-password option below with the invite you were sent — or contact your administrator.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Other errors */}
        {error && !isDomainError && (
          <div className="bg-red-50 text-red-600 text-sm p-3 rounded border border-red-200 mb-4">{error}</div>
        )}

        {/* Success message after signup */}
        {signupSuccess && (
          <div className="bg-green-50 text-green-700 text-sm p-3 rounded border border-green-200 mb-4">
            ✓ {signupSuccess}
          </div>
        )}

        {mode === 'reset-sent' ? (
          <div className="text-center">
            <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-4">
              <p>Password reset link sent!</p>
              <p className="text-sm mt-1">Check your email for instructions to reset your password.</p>
            </div>
            <button
              onClick={goToDefaultScreen}
              className="hover:underline text-sm"
              style={{ color: displayNavy }}
            >
              Back to sign in
            </button>
          </div>
        ) : !showEmailLogin && mode === 'sso' ? (
          /* Method picker — shows only the sign-in method(s) this org has enabled */
          <>
            {ssoEnabled && (
              <div className={`rounded-xl border-2 border-gray-200 p-5 ${passwordAuthEnabled ? 'mb-4' : ''}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: '#EFF4FB', color: displayNavy }}>
                    Landmark employees
                  </span>
                </div>
                <p className="text-sm mb-3" style={{ color: displaySecondary }}>
                  Use your Microsoft 365 work account to sign in.
                </p>
                <button
                  onClick={handleMicrosoftSignIn}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: LFG_COLORS.navy }}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MsLogo className="w-4 h-4" />}
                  {loading ? 'Signing in…' : 'Continue with Microsoft 365'}
                </button>
              </div>
            )}

            {passwordAuthEnabled && (
              <div className="rounded-xl border-2 border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: '#FEF3C7', color: '#A16207' }}>
                    External access
                  </span>
                </div>
                <p className="text-sm mb-3" style={{ color: displaySecondary }}>
                  Counsel, accountants, brokers, board members — use the email and password from your invitation.
                </p>
                <button
                  onClick={() => { setShowEmailLogin(true); setMode('login'); setError(''); }}
                  className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg font-medium border-2 hover:bg-gray-50 transition-colors"
                  style={{ color: displayNavy, borderColor: '#E5E9F0' }}
                >
                  Sign in with email and password
                </button>
              </div>
            )}
          </>
        ) : (
          /* Email/Password Backup View */
          <>
            {mode === 'signup' && inviteData && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="font-medium text-blue-800">Create Your Account</p>
                <p className="text-sm text-blue-700 mt-1">
                  Set a password below to access Landmark Legacy Room. Your email <strong>{inviteData.email}</strong> will be your username.
                </p>
              </div>
            )}
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-4">
              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: displayNavy }}>Your Name</label>
                  <input 
                    type="text" 
                    value={loginForm.fullName} 
                    onChange={e => setLoginForm(p => ({ ...p, fullName: e.target.value }))} 
                    className="w-full border rounded px-3 py-2 focus:ring-2 focus:outline-none" 
                    placeholder="John Smith"
                    autoComplete="name"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: displayNavy }}>Email</label>
                <input 
                  type="email" 
                  value={loginForm.email} 
                  onChange={e => setLoginForm(p => ({ ...p, email: e.target.value }))} 
                  className="w-full border rounded px-3 py-2 focus:ring-2 focus:outline-none" 
                  placeholder="you@company.com" 
                  readOnly={mode === 'signup' && inviteData}
                  style={mode === 'signup' && inviteData ? { backgroundColor: '#f9fafb' } : {}}
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                />
                {mode === 'signup' && inviteData && (
                  <p className="text-xs mt-1" style={{ color: displaySecondary }}>
                    Email from your invitation
                  </p>
                )}
              </div>
              {mode !== 'reset' && (
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: displayNavy }}>Password</label>
                  <input 
                    type="password" 
                    value={loginForm.password} 
                    onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))} 
                    className="w-full border rounded px-3 py-2 focus:ring-2 focus:outline-none" 
                    placeholder="********"
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  />
                  {mode === 'signup' && (
                    <p className="text-xs mt-1" style={{ color: displaySecondary }}>
                      8+ characters with uppercase, lowercase, and a number
                    </p>
                  )}
                </div>
              )}
              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: displayNavy }}>Confirm Password</label>
                  <input 
                    type="password" 
                    value={loginForm.confirmPassword} 
                    onChange={e => setLoginForm(p => ({ ...p, confirmPassword: e.target.value }))} 
                    className="w-full border rounded px-3 py-2 focus:ring-2 focus:outline-none" 
                    placeholder="********"
                    autoComplete="new-password"
                  />
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full text-white py-2.5 rounded font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 transition-opacity"
                style={{ backgroundColor: LFG_COLORS.navy }}
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {mode === 'signup' ? 'Create Account' : mode === 'reset' ? 'Send Reset Link' : 'Sign In'}
              </button>
            </form>
            <div className="mt-6 text-center space-y-2">
              {mode === 'login' && (
                <button 
                  type="button"
                  onClick={() => { setMode('reset'); setError(''); }} 
                  className="hover:underline text-sm block w-full"
                  style={{ color: displaySecondary }}
                >
                  Forgot your password?
                </button>
              )}
              {mode === 'signup' && (
                <button 
                  type="button"
                  onClick={() => { setMode('login'); setInviteData(null); setError(''); }} 
                  className="hover:underline text-sm"
                  style={{ color: displayNavy }}
                >
                  Already have an account? Sign in
                </button>
              )}
              {(mode === 'reset' || mode === 'login') && !inviteData && ssoEnabled && (
                <button
                  type="button"
                  onClick={goToDefaultScreen}
                  className="hover:underline text-sm block w-full"
                  style={{ color: displayNavy }}
                >
                  Back to Microsoft 365 sign in
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// BOOTSTRAP WIZARD — first-run setup when no organization exists yet for
// this deployment. Replaces the old manual `SELECT setup_first_admin(...)`
// SQL step with a self-service flow: pick Microsoft 365 or email/password,
// name the organization, land as its first admin.
// ============================================================================
function BootstrapWizard({ user, ssoEnabled, signInWithMicrosoft, bootstrapSignUp, bootstrapOrganization, signOut, showToast }) {
  const { displayPrimary, displaySecondary, displayNavy } = useDisplayColors(null, null);
  // 'choose' (pick a sign-in method) — skipped entirely if the user already
  // has a live session (they got here via an SSO redirect).
  // 'details' (org name + admin name, plus email/password if that's the path)
  // 'confirm-email' (password path only, if the project requires email confirmation)
  const [step, setStep] = useState(user ? 'details' : 'choose');
  const [authMethod, setAuthMethod] = useState(user ? 'sso' : null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ orgName: '', adminName: '', email: '', password: '', confirmPassword: '' });

  const handleChooseMicrosoft = async () => {
    setLoading(true);
    setError('');
    const { error } = await signInWithMicrosoft();
    if (error) {
      setError(error.message || 'Failed to sign in with Microsoft. Please try again.');
      setLoading(false);
    }
    // On success, the OAuth redirect takes over — this component remounts
    // with `user` set once it completes.
  };

  const handleSubmitDetails = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');

    if (!form.orgName.trim()) {
      setError('Organization name is required.');
      return;
    }

    setLoading(true);
    try {
      if (authMethod === 'password' && !user) {
        if (form.password !== form.confirmPassword) throw new Error('Passwords do not match.');
        if (form.password.length < 8) throw new Error('Password must be at least 8 characters.');
        if (!/[A-Z]/.test(form.password) || !/[a-z]/.test(form.password) || !/[0-9]/.test(form.password)) {
          throw new Error('Password must include uppercase, lowercase, and a number.');
        }
        const signUpResult = await bootstrapSignUp(form.email, form.password, form.adminName);
        if (signUpResult.error) throw signUpResult.error;
        if (signUpResult.needsEmailConfirmation) {
          setStep('confirm-email');
          setLoading(false);
          return;
        }
      }

      const result = await bootstrapOrganization({ orgName: form.orgName.trim(), adminName: form.adminName.trim() || undefined });
      if (result.error) throw result.error;
      // Success — App.jsx's orgExists flips true and re-renders into the
      // normal dashboard automatically, nothing further to do here.
      if (showToast) showToast(`${form.orgName.trim()} is set up — welcome!`, 'success');
    } catch (err) {
      setError(err.message || 'Setup failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-full" style={{ backgroundColor: `${displayNavy}15` }}>
              <Rocket className="w-8 h-8" style={{ color: displayNavy }} />
            </div>
          </div>
          <h1 className="text-2xl font-bold" style={{ color: displayNavy }}>Set up your workspace</h1>
          <p className="text-sm mt-2" style={{ color: displaySecondary }}>
            Nobody's set this up yet — let's create your organization and get you in as the first admin.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 text-sm p-3 rounded border border-red-200 mb-4">{error}</div>
        )}

        {step === 'confirm-email' ? (
          <div className="text-center">
            <div className="bg-blue-50 text-blue-700 p-4 rounded-lg">
              <p className="font-medium">Check your email</p>
              <p className="text-sm mt-1">We sent a confirmation link to <strong>{form.email}</strong>. Click it, then come back here to finish setting up your organization.</p>
            </div>
          </div>
        ) : step === 'choose' ? (
          <div className="space-y-4">
            {ssoEnabled !== false && (
              <button
                onClick={handleChooseMicrosoft}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                style={{ backgroundColor: displayNavy }}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MsLogo className="w-4 h-4" />}
                {loading ? 'Signing in…' : 'Set up with Microsoft 365'}
              </button>
            )}
            <button
              onClick={() => { setAuthMethod('password'); setStep('details'); setError(''); }}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg font-medium border-2 hover:bg-gray-50 transition-colors"
              style={{ color: displayNavy, borderColor: '#E5E9F0' }}
            >
              Set up with email &amp; password
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmitDetails} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: displayNavy }}>Organization name</label>
              <input
                type="text"
                value={form.orgName}
                onChange={e => setForm(p => ({ ...p, orgName: e.target.value }))}
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:outline-none"
                placeholder="Acme Holdings"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: displayNavy }}>Your name</label>
              <input
                type="text"
                value={form.adminName}
                onChange={e => setForm(p => ({ ...p, adminName: e.target.value }))}
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:outline-none"
                placeholder="Jane Smith"
                autoComplete="name"
              />
            </div>
            {authMethod === 'password' && !user && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: displayNavy }}>Your email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    className="w-full border rounded px-3 py-2 focus:ring-2 focus:outline-none"
                    placeholder="you@company.com"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: displayNavy }}>Password</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                    className="w-full border rounded px-3 py-2 focus:ring-2 focus:outline-none"
                    placeholder="********"
                    autoComplete="new-password"
                  />
                  <p className="text-xs mt-1" style={{ color: displaySecondary }}>8+ characters with uppercase, lowercase, and a number</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: displayNavy }}>Confirm password</label>
                  <input
                    type="password"
                    value={form.confirmPassword}
                    onChange={e => setForm(p => ({ ...p, confirmPassword: e.target.value }))}
                    className="w-full border rounded px-3 py-2 focus:ring-2 focus:outline-none"
                    placeholder="********"
                    autoComplete="new-password"
                  />
                </div>
              </>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full text-white py-2.5 rounded font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 transition-opacity"
              style={{ backgroundColor: displayNavy }}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Create organization
            </button>
            <div className="text-center space-y-2">
              {!user && (
                <button
                  type="button"
                  onClick={() => { setStep('choose'); setAuthMethod(null); setError(''); }}
                  className="hover:underline text-sm block w-full"
                  style={{ color: displaySecondary }}
                >
                  Back
                </button>
              )}
              {user && (
                <button
                  type="button"
                  onClick={signOut}
                  className="hover:underline text-sm block w-full"
                  style={{ color: displaySecondary }}
                >
                  Not you? Sign out
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAGIC-LINK INTERSTITIAL (shown for invite links routed through
// link_token/link_type instead of Supabase's raw one-click verify link —
// see pendingAuthLink in AppInner for why). Requires an explicit click
// before calling verifyOtp, so an email-scanner's automated GET on this
// page can't consume the single-use token the way it could with a bare
// action_link.
// ============================================================================
function MagicLinkInterstitial({ tokenHash, tokenType, email, resetPassword, onConsumed }) {
  const { displayNavy, displaySecondary } = useDisplayColors(null, null);
  const [status, setStatus] = useState('idle'); // idle | verifying | error
  const [errorMsg, setErrorMsg] = useState('');
  const [resendEmail, setResendEmail] = useState(email || '');
  const [resendStatus, setResendStatus] = useState('idle'); // idle | sending | sent

  const handleSignIn = async () => {
    setStatus('verifying');
    setErrorMsg('');
    try {
      const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: tokenType });
      if (error) throw error;
      onConsumed();
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'This link has expired or was already used.');
    }
  };

  const handleResend = async () => {
    if (!resendEmail || resendStatus === 'sending') return;
    setResendStatus('sending');
    await resetPassword(resendEmail);
    // request-password-reset always reports success (account-enumeration
    // defense) — same reason the copy below doesn't promise a match was found.
    setResendStatus('sent');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src={LFG_LOGO_URL}
              alt="LFG Logo"
              className="h-16 w-auto object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: displayNavy }}>Sign in to continue</h1>
          {email && (
            <p className="text-sm mt-1" style={{ color: displaySecondary }}>
              Signing in as <span className="font-medium">{email}</span>
            </p>
          )}
        </div>

        {status === 'error' ? (
          <div className="space-y-3 text-center">
            <p className="text-sm text-red-600">{errorMsg}</p>
            {resendStatus === 'sent' ? (
              <div className="bg-green-50 text-green-700 p-4 rounded-lg text-left">
                <p className="text-sm font-medium">Check your email</p>
                <p className="text-xs mt-1">If {resendEmail} has an invite on file, we just sent a fresh link to set your password.</p>
              </div>
            ) : (
              <div className="space-y-2 text-left">
                <p className="text-xs" style={{ color: displaySecondary }}>
                  This link may have expired or already been used. Enter your email and we'll send you a new one.
                </p>
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm"
                />
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={!resendEmail || resendStatus === 'sending'}
                  className="w-full h-10 rounded-lg text-white font-medium text-sm transition-all hover:shadow-md disabled:opacity-60"
                  style={{ backgroundColor: displayNavy }}
                >
                  {resendStatus === 'sending' ? 'Sending…' : 'Send me a new link'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={handleSignIn}
            disabled={status === 'verifying'}
            className="w-full h-11 rounded-lg text-white font-medium transition-all hover:shadow-md disabled:opacity-60"
            style={{ backgroundColor: displayNavy }}
          >
            {status === 'verifying' ? (
              <span className="inline-flex items-center gap-2 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
              </span>
            ) : 'Click to sign in'}
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// PASSWORD RESET FORM (shown after clicking reset link in email)
// ============================================================================
function PasswordResetForm({ onComplete, showToast }) {
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(null, null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters with uppercase, lowercase, and a number');
      return;
    }
    
    setLoading(true);
    
    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) throw error;

      showToast('Password updated successfully!');
      // updateUser() fires a USER_UPDATED event, which useAuth's listener
      // doesn't handle — and the PASSWORD_RECOVERY event before it
      // deliberately skips fetching userProfile/organization (we're
      // mid-flow at that point). Net effect: without a reload, userProfile
      // stays null forever after setting a password, every role check
      // (isDealAdvisor, isViewer, etc.) silently defaults to false, and
      // every org-scoped query returns nothing — the user lands in a
      // fully unrestricted-looking shell with 0 deals and everything
      // spinning. A hard reload re-runs the proven-correct initial
      // session-check path instead of extending the auth event state
      // machine further.
      window.location.reload();
    } catch (err) {
      setError(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src={LFG_LOGO_URL} 
              alt="LFG Logo" 
              className="h-16 w-auto object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: displayNavy }}>Set New Password</h1>
          <p className="text-sm mt-1" style={{ color: displaySecondary }}>Enter your new password below</p>
        </div>
        
        <form onSubmit={handleSubmit} action="#" method="POST" className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: displayNavy }}>New Password</label>
            <input 
              type="password" 
              value={password} 
              onChange={e => setPassword(e.target.value)} 
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:outline-none" 
              placeholder="********" 
              required 
              minLength={6}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: displayNavy }}>Confirm Password</label>
            <input 
              type="password" 
              value={confirmPassword} 
              onChange={e => setConfirmPassword(e.target.value)} 
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:outline-none" 
              placeholder="********" 
              required 
              minLength={6}
            />
          </div>
          {error && <div className="bg-red-50 text-red-600 text-sm p-3 rounded border border-red-200">{error}</div>}
          <button 
            type="submit" 
            disabled={loading} 
            className="w-full text-white py-2.5 rounded font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 transition-opacity"
            style={{ backgroundColor: LFG_COLORS.navy }}
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Update Password
          </button>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// APP SHELL (Option B) — fixed left sidebar navigation
// ============================================================================

// Canonical view list. Rendered top-to-bottom in the sidebar in this order.
// Alfie: group deal-related nav separately from org-wide/admin utilities.
const NAV_ITEMS = [
  { id: 'portfolio', label: 'Portfolio', icon: Briefcase,  roles: 'all',  group: 'deal' },
  { id: 'timeline',  label: 'Timeline',  icon: Calendar,   roles: 'all',  group: 'deal', hideForDealAdvisor: true },
  { id: 'map',       label: 'Map',       icon: MapPin,     roles: 'all',  group: 'deal', hideForDealAdvisor: true },
  { id: 'files',     label: 'Files',     icon: FileText,   roles: 'team', group: 'deal', hideForIntegration: true },
  { id: 'discovery', label: 'Prospects', icon: Sparkles,   roles: 'team', group: 'deal', accent: true, hideForIntegration: true },
  { id: 'activity',  label: 'Activity',  icon: Activity,   roles: 'team', group: 'other' },
  { id: 'vendors',   label: 'Vendors',   icon: Building2,  roles: 'team', group: 'other' },
  { id: 'orgchart',  label: 'Org chart', icon: Users,      roles: 'internal', group: 'other' },
  { id: 'entities',  label: 'Entities',  icon: Landmark,   roles: 'team', group: 'other', hideForIntegration: true },
  { id: 'finance',   label: 'Finance',   icon: DollarSign, roles: 'team', group: 'other', hideForIntegration: true },
  { id: 'market',    label: 'Operating Areas', icon: Activity, roles: 'team', group: 'other', hideForIntegration: true },
  { id: 'opportunity', label: 'New County Targets', icon: Target, roles: 'team', group: 'other', hideForIntegration: true },
];

// Single nav item in the sidebar. Active = filled navy (or violet for
// accent) gradient pill. Inactive = transparent with hover slate-tint.
function SidebarNavItem({ icon: Icon, label, isActive, accent, onClick }) {
  const accentColor = accent ? '#7C3AED' : '#1A3665';
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full h-9 flex items-center gap-3 px-3 text-[13px] font-medium rounded-lg transition-all"
      style={isActive ? {
        background: `linear-gradient(135deg, ${accentColor} 0%, ${accent ? '#A78BFA' : '#2E4B7C'} 100%)`,
        color: 'white',
        boxShadow: `0 1px 2px ${accentColor}55, 0 2px 4px ${accentColor}22`,
      } : {
        backgroundColor: 'transparent',
        color: '#475569',
      }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(15, 23, 42, 0.04)'; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      {Icon && <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2.2} />}
      <span className="truncate">{label}</span>
    </button>
  );
}

// Sidebar menu item used in the user popup at the bottom of the sidebar.
function SidebarMenuItem({ icon: Icon, label, onClick, destructive }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full h-9 flex items-center gap-2.5 px-3 text-[13px] transition-colors text-left"
      style={{ color: destructive ? '#DC2626' : '#334155' }}
      onMouseEnter={e => { e.currentTarget.style.backgroundColor = destructive ? '#FEF2F2' : '#FAFBFD'; }}
      onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />}
      {label}
    </button>
  );
}

// AppShell — fixed left sidebar + main content area. Every top-level
// peer view (Portfolio, Timeline, Map, Files, Activity, Vendors,
// Entities, Finance, Prospects) renders its content inside <AppShell>
// so nav is always visible and consistent.
//
// DealView and the seller portal do NOT use AppShell — they have their
// own chrome (tabs, scoped header) and full-width needs.
function AppShell({
  organization, userProfile, pc, currentView, setView, setDarkMode, darkMode,
  signOut, isAdmin, isViewer, isLimitedViewer, isDealAdvisor, isIntegration, isBoardMember,
  onNewDeal, onFromFiles, onExportCsv, onExportPdf, onExportFootprintCsv, onExportTasksCsv,
  toast,
  children,
}) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = React.useRef(null);
  // On mobile (< 640px), sidebar always starts collapsed and acts as an overlay.
  const isMobileWidth = () => typeof window !== 'undefined' && window.innerWidth < 640;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (isMobileWidth()) return true;
    try { return localStorage.getItem('lfg.sidebarCollapsed') === '1'; } catch { return false; }
  });
  const [isMobile, setIsMobile] = useState(isMobileWidth);
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 640;
      setIsMobile(mobile);
      if (mobile) setSidebarCollapsed(true);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    if (!isMobile) {
      try { localStorage.setItem('lfg.sidebarCollapsed', sidebarCollapsed ? '1' : '0'); } catch {}
    }
  }, [sidebarCollapsed, isMobile]);
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  const visibleNav = NAV_ITEMS.filter(item => {
    if (isIntegration && item.hideForIntegration) return false;
    if (isDealAdvisor && item.hideForDealAdvisor) return false;
    if (item.roles === 'all') return true;
    if (item.roles === 'team') return !isLimitedViewer && !isDealAdvisor;
    // 'internal' = hide only from deal_advisor (limited_viewer + board_member + everyone else can see).
    if (item.roles === 'internal') return !isDealAdvisor;
    return false;
  });

  return (
    <div className="w-full min-h-screen relative flex"
         style={{
           background: 'linear-gradient(165deg, #A8C0DE 0%, #C2D4EC 8%, #D6E2F2 18%, #E5ECF6 32%, #EFF2F8 55%, #F6F8FB 80%, #FAFBFD 100%)',
         }}
    >
      {/* Radial atmosphere washes (same as DealView) */}
      <div className="fixed pointer-events-none z-0" style={{
        top: '-15%', left: '-10%', width: '55%', height: '80%',
        background: 'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0.15) 40%, transparent 65%)',
        filter: 'blur(20px)',
      }} />
      <div className="fixed pointer-events-none z-0" style={{
        top: '-10%', right: '-10%', width: '50%', height: '70%',
        background: 'radial-gradient(ellipse at center, rgba(26, 54, 101, 0.15) 0%, rgba(26, 54, 101, 0.06) 35%, transparent 65%)',
        filter: 'blur(50px)',
      }} />

      {/* Mobile backdrop — tap to close sidebar */}
      {isMobile && !sidebarCollapsed && (
        <div
          className="fixed inset-0 z-40 bg-black/30"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      {/* Sidebar — fixed, 240px wide. Slides out when collapsed.
          On mobile it overlays the content instead of pushing it. */}
      <aside
        className="w-60 fixed top-0 bottom-0 left-0 flex flex-col"
        style={{
          zIndex: isMobile ? 50 : 30,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'saturate(160%) blur(14px)',
          WebkitBackdropFilter: 'saturate(160%) blur(14px)',
          borderRight: '1px solid rgba(229, 233, 240, 0.8)',
          transform: sidebarCollapsed ? 'translateX(-100%)' : 'translateX(0)',
          transition: 'transform 200ms ease',
        }}
      >
        {/* Header — logo stacked over org name, full sidebar width */}
        <button
          type="button"
          onClick={() => setView('portfolio')}
          className="flex flex-col items-center gap-3 px-4 pt-6 pb-5 flex-shrink-0 transition-opacity hover:opacity-80"
        >
          <img
            src={organization?.logo_url || LFG_LOGO_URL}
            alt="Logo"
            className="w-full h-auto max-h-20 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="text-center">
            <div
              className="text-[13px] font-semibold tracking-tight leading-tight"
              style={{
                background: 'linear-gradient(135deg, #0F172A 0%, #1A3665 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.01em',
              }}
            >
              {organization?.name || 'LFG Portal'}
            </div>
            <div className="text-[10px] font-medium tracking-wider uppercase mt-0.5" style={{ color: '#94A3B8' }}>
              Acquisitions
            </div>
          </div>
        </button>

        {/* Nav list — grouped Deal vs Other per Alfie's feedback */}
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {['deal', 'other'].map(groupKey => {
            const groupItems = visibleNav.filter(item => (item.group || 'other') === groupKey);
            if (groupItems.length === 0) return null;
            return (
              <div key={groupKey} className={groupKey === 'other' ? 'mt-3' : ''}>
                <div className="text-[10px] font-semibold uppercase tracking-[0.1em] px-3 pt-2 pb-2" style={{ color: '#94A3B8' }}>
                  {groupKey === 'deal' ? 'Deals' : 'Other'}
                </div>
                {groupItems.map(item => (
                  <SidebarNavItem
                    key={item.id}
                    icon={item.icon}
                    label={item.label}
                    isActive={currentView === item.id}
                    accent={item.accent}
                    onClick={() => { setView(item.id); if (isMobile) setSidebarCollapsed(true); }}
                  />
                ))}
              </div>
            );
          })}
        </nav>

        {/* Bottom — primary actions + user */}
        <div className="px-3 py-3 space-y-2 flex-shrink-0"
             style={{ borderTop: '1px solid rgba(229, 233, 240, 0.8)' }}>
          {!isViewer && !isDealAdvisor && onNewDeal && (
            <button
              type="button"
              onClick={onNewDeal}
              className="w-full h-9 inline-flex items-center justify-center gap-2 text-[13px] font-semibold rounded-lg text-white transition-all hover:shadow-md"
              style={{
                background: `linear-gradient(135deg, ${pc} 0%, #2E4B7C 100%)`,
                boxShadow: `0 1px 2px ${pc}40, 0 2px 4px ${pc}20`,
              }}
            >
              <Plus className="w-4 h-4" /> New Deal
            </button>
          )}
          {!isViewer && !isDealAdvisor && onFromFiles && (
            <button
              type="button"
              onClick={onFromFiles}
              className="w-full h-9 inline-flex items-center justify-center gap-2 text-[13px] font-medium rounded-lg transition-all"
              style={{
                backgroundColor: 'rgba(124, 58, 237, 0.08)',
                color: '#7C3AED',
                border: '1px solid rgba(124, 58, 237, 0.2)',
              }}
            >
              <Sparkles className="w-4 h-4" /> From Files
            </button>
          )}

          {/* User menu trigger */}
          <div ref={userMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setUserMenuOpen(m => !m)}
              className="w-full h-10 flex items-center gap-2.5 px-2 rounded-lg transition-colors"
              style={{
                backgroundColor: userMenuOpen ? 'rgba(15, 23, 42, 0.06)' : 'transparent',
              }}
              onMouseEnter={e => { if (!userMenuOpen) e.currentTarget.style.backgroundColor = 'rgba(15, 23, 42, 0.04)'; }}
              onMouseLeave={e => { if (!userMenuOpen) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0"
                   style={{ background: `linear-gradient(135deg, ${pc} 0%, #2E4B7C 100%)`, color: 'white' }}>
                {(userProfile?.full_name || userProfile?.email || '?').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-[12px] font-semibold truncate" style={{ color: '#0F172A' }}>
                  {userProfile?.full_name || 'User'}
                </div>
                <div className="text-[10px] truncate" style={{ color: '#94A3B8' }}>
                  {userProfile?.email}
                </div>
              </div>
              <ChevronDown className="w-3 h-3 flex-shrink-0" style={{ color: '#94A3B8' }} />
            </button>

            {userMenuOpen && (
              <div
                className="absolute bottom-full left-0 right-0 mb-2 rounded-xl overflow-hidden py-1"
                style={{
                  backgroundColor: darkMode ? '#1a1f2e' : 'white',
                  border: darkMode ? '1px solid #2a3140' : '1px solid #E5E9F0',
                  boxShadow: darkMode
                    ? '0 10px 30px rgba(0, 0, 0, 0.5), 0 4px 10px rgba(0, 0, 0, 0.3)'
                    : '0 10px 30px rgba(15, 23, 42, 0.12), 0 4px 10px rgba(15, 23, 42, 0.06)',
                }}
              >
                {currentView === 'portfolio' && onExportCsv && !isDealAdvisor && (
                  <SidebarMenuItem icon={Download} label="Export CSV" onClick={() => { setUserMenuOpen(false); onExportCsv(); }} />
                )}
                {currentView === 'portfolio' && onExportPdf && !isDealAdvisor && (
                  <SidebarMenuItem icon={FileDown} label="Export PDF" onClick={() => { setUserMenuOpen(false); onExportPdf(); }} />
                )}
                {currentView === 'portfolio' && onExportFootprintCsv && !isDealAdvisor && (
                  <SidebarMenuItem icon={MapPin} label="Export Footprint CSV" onClick={() => { setUserMenuOpen(false); onExportFootprintCsv(); }} />
                )}
                {currentView === 'portfolio' && onExportTasksCsv && !isDealAdvisor && (
                  <SidebarMenuItem icon={Download} label="Export Tasks CSV" onClick={() => { setUserMenuOpen(false); onExportTasksCsv(); }} />
                )}
                {(isAdmin || isBoardMember || userProfile?.role === 'member') && (
                  <SidebarMenuItem
                    icon={Settings}
                    label={
                      isBoardMember && !isAdmin
                        ? 'Settings (read-only)'
                        : (!isAdmin && userProfile?.role === 'member' ? 'Tools' : 'Settings')
                    }
                    onClick={() => {
                      setUserMenuOpen(false);
                      // Members land on Import Deals by default since the
                      // config tabs are hidden for them.
                      if (!isAdmin && !isBoardMember && userProfile?.role === 'member') {
                        setAdminTab('import');
                      }
                      setView('admin');
                    }}
                  />
                )}
                <SidebarMenuItem
                  icon={darkMode ? Sun : Moon}
                  label={darkMode ? 'Light mode' : 'Dark mode'}
                  onClick={() => { setUserMenuOpen(false); setDarkMode(d => !d); }}
                />
                <div style={{ borderTop: darkMode ? '1px solid #2a3140' : '1px solid #F0F2F6' }}>
                  <SidebarMenuItem icon={LogOut} label="Sign out" onClick={() => { setUserMenuOpen(false); signOut(); }} destructive />
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Sidebar toggle — fixed handle, always visible.
          On mobile: larger tap target (36px), always positioned at left:12 since
          the sidebar is an overlay and the content doesn't shift. */}
      <button
        type="button"
        onClick={() => setSidebarCollapsed(c => !c)}
        title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
        aria-label={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
        style={{
          position: 'fixed',
          top: isMobile ? 12 : 16,
          left: (isMobile || sidebarCollapsed) ? 12 : '224px',
          zIndex: 60,
          width: isMobile ? 36 : 28,
          height: isMobile ? 36 : 28,
          borderRadius: isMobile ? 8 : 6,
          backgroundColor: 'white',
          border: '1px solid #E5E9F0',
          boxShadow: '0 1px 3px rgba(15,23,42,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: '#475569',
          transition: 'left 200ms ease',
        }}
      >
        {sidebarCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
      </button>

      {/* Main content — on mobile sidebar is an overlay so no margin shift;
          on desktop margin shifts with sidebar collapse state. */}
      <main className="flex-1 min-w-0 relative z-10" style={{
        marginLeft: isMobile ? 0 : (sidebarCollapsed ? 0 : '15rem'),
        transition: 'margin-left 200ms ease',
      }}>
        {children}
      </main>

      {/* Toast — rendered here (not per-view) so every AppShell-wrapped view
          (deal, timeline, map, admin, etc.) actually shows it. Previously
          each view had to render its own toast block and most didn't,
          so actions like "Grant Access" on a deal page silently set toast
          state with nothing on screen to display it. z-[100] keeps it above
          modals (z-50) and the sidebar toggle (z-60). */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-[100] px-4 py-3 rounded-lg shadow-lg text-white ${
          toast.type === 'error' ? 'bg-red-500' : ''
        }`} style={{ backgroundColor: toast.type === 'error' ? undefined : LFG_COLORS.green }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PORTFOLIO VIEW — shared helpers
// ============================================================================

// Unified nav/action button for the portfolio header. Mirrors the
// HeaderActionBtn style used in DealView so the whole app feels coherent.
// `iconOnly` collapses the button to a 36×36 square with tooltip — used
// for the nav-bar row where 7+ labelled buttons would wrap awkwardly.
function PortfolioNavBtn({ icon: Icon, label, onClick, variant = 'secondary', accent, iconOnly = false, title }) {
  const isPrimary = variant === 'primary';
  const isAccent = variant === 'accent';
  const sharedStyle = isPrimary ? {
    background: `linear-gradient(135deg, ${accent} 0%, #2E4B7C 100%)`,
    color: 'white',
    boxShadow: `0 1px 2px ${accent}40, 0 2px 4px ${accent}20`,
  } : isAccent ? {
    background: `linear-gradient(135deg, ${accent} 0%, ${accent}E6 100%)`,
    color: 'white',
    boxShadow: `0 1px 2px ${accent}55`,
  } : {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    color: '#334155',
    border: '1px solid rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(6px)',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  };

  if (iconOnly) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title || label}
        className="h-9 w-9 inline-flex items-center justify-center rounded-lg transition-all disabled:opacity-50"
        style={sharedStyle}
      >
        {Icon && <Icon className="w-4 h-4" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="h-9 inline-flex items-center gap-1.5 px-3 text-sm font-medium rounded-lg transition-all disabled:opacity-50"
      style={sharedStyle}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </button>
  );
}

// Stat tile matching DealView hero/activity. Optional trend pill, optional
// small icon. Used in the summary row.
function PortfolioStatTile({ label, value, accent = '#0F172A', icon: Icon, accentBg }) {
  return (
    <div
      className="rounded-xl px-3 py-2 transition-all hover:-translate-y-0.5"
      style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFD 100%)',
        border: '1px solid #E5E9F0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.02)',
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-[0.1em] truncate" style={{ color: '#94A3B8' }}>
            {label}
          </div>
          <div className="text-xl font-semibold mt-0.5 tabular-nums leading-none" style={{ color: accent, letterSpacing: '-0.015em' }}>
            {value}
          </div>
        </div>
        {Icon && (
          <div
            className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: accentBg || `${accent}15`, color: accent }}
          >
            <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
          </div>
        )}
      </div>
    </div>
  );
}

// Portfolio taxonomy rework — inline green/yellow/red status dot on
// Active-section deal rows (staleTier is computed in categorizedDeals,
// only present on active-bucket deals). Replaces the old "Stalled/At
// Risk" sub-bucket: a deal stays in Active regardless of staleness, and
// this is how you explain *why* it's stale instead of it silently
// dropping to a different section. Clicking opens a small popover to
// set a delay-reason category + optional note; saves directly via
// supabase (useDeals' realtime subscription picks up the change).
function DealStatusDot({ deal, delayCategories }) {
  const [open, setOpen] = useState(false);
  const [categoryId, setCategoryId] = useState(deal.delay_reason_category_id || '');
  const [note, setNote] = useState(deal.delay_reason_note || '');
  const [saving, setSaving] = useState(false);
  const color = { green: '#22C55E', yellow: '#EAB308', red: '#EF4444' }[deal.staleTier] || '#22C55E';

  const handleSave = async () => {
    setSaving(true);
    try {
      await supabase.from('deals').update({
        delay_reason_category_id: categoryId || null,
        delay_reason_note: note.trim() || null,
      }).eq('id', deal.id);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <span className="relative inline-flex flex-shrink-0">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: color }}
        title={deal.staleTier === 'green' ? 'Updated recently' : `${deal.daysSinceUpdate}d since last update — click to set a reason`}
      />
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div
            className="absolute z-20 top-4 left-0 w-64 rounded-lg shadow-lg p-3 space-y-2"
            style={{ backgroundColor: 'white', border: '1px solid #E5E9F0' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>
              {deal.daysSinceUpdate}d since last update
            </div>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full text-sm rounded px-2 py-1"
              style={{ border: '1px solid #E5E9F0', color: '#0F172A' }}
            >
              <option value="">— Reason for delay —</option>
              {delayCategories.map(c => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add color (optional)"
              rows={2}
              className="w-full text-sm rounded px-2 py-1 resize-none"
              style={{ border: '1px solid #E5E9F0', color: '#0F172A' }}
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="text-xs px-2 py-1 rounded" style={{ color: '#64748B' }}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="text-xs px-2 py-1 rounded text-white disabled:opacity-60"
                style={{ backgroundColor: '#1A3665' }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </>
      )}
    </span>
  );
}

// Phase C.5.2 — Territory create/edit modal. Tight scope: name, color,
// and a state multi-select. County picker is deferred — schema has the
// column reserved for when we ship county-level precision.
const TERRITORY_COLORS = [
  '#1A3665', // navy
  '#7C3AED', // violet
  '#10B981', // green
  '#0891B2', // cyan
  '#D97706', // amber
  '#DB2777', // pink
  '#475569', // slate
  '#EA580C', // orange
];

// All US state codes + DC. Keeps the picker predictable regardless of
// which states currently appear in the pipeline.
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM',
  'NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA',
  'WV','WI','WY'
];

function TerritoryEditorModal({ territory, availableStates, lfgLocations, onClose, onSave, onDelete, pc }) {
  const [name, setName] = useState(territory?.name || '');
  const [description, setDescription] = useState(territory?.description || '');
  const [color, setColor] = useState(territory?.color || TERRITORY_COLORS[0]);
  const [states, setStates] = useState(new Set(territory?.states || []));
  const [polygon, setPolygon] = useState(territory?.drawn_geometry || null);
  const [useStateFallback, setUseStateFallback] = useState(!territory?.drawn_geometry && (territory?.states?.length > 0));
  const [saving, setSaving] = useState(false);

  const hasPolygon = polygon && polygon.type === 'Polygon' && polygon.coordinates?.[0]?.length >= 4;
  // A valid geo definition requires either a polygon OR ≥1 state in fallback mode.
  const geoValid = hasPolygon || (useStateFallback && states.size > 0);

  const toggle = (code) => {
    setStates(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim() || !geoValid) return;
    setSaving(true);
    try {
      // Derive county_fips from the polygon by point-in-polygon testing every
      // US county centroid against the drawn shape. The Market Share view's
      // By Region aggregation reads territory.county_fips; without this
      // step a polygon-only territory would have no counties to aggregate.
      let derivedCountyFips = [];
      if (hasPolygon) {
        for (const c of US_COUNTY_CENTROIDS) {
          if (pointInPolygon(c.lat, c.lng, polygon)) derivedCountyFips.push(c.fips);
        }
      }
      await onSave({
        name: name.trim(),
        description: description.trim() || null,
        color,
        // Send both; backend stores both. Filter logic matches on EITHER.
        states: useStateFallback ? Array.from(states) : [],
        county_fips: derivedCountyFips,
        drawn_geometry: hasPolygon ? polygon : null,
      });
    } finally { setSaving(false); }
  };

  // Convert LFG locations with coords into reference points for the editor.
  const referencePoints = useMemo(() => {
    return (lfgLocations || [])
      .filter(l => l.latitude != null && l.longitude != null)
      .map(l => ({ lat: Number(l.latitude), lng: Number(l.longitude) }));
  }, [lfgLocations]);

  // Sort: states with deals first (alphabetical within), then the rest
  const withDealsSet = new Set(availableStates.map(s => s.code));
  const sortedStates = [
    ...US_STATES.filter(s => withDealsSet.has(s)),
    ...US_STATES.filter(s => !withDealsSet.has(s)),
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
           onClick={e => e.stopPropagation()}
           style={{ boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid #F0F2F6' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                 style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`, boxShadow: `0 2px 6px ${color}40` }}>
              <MapPin className="w-4 h-4 text-white" strokeWidth={2.2} />
            </div>
            <h2 className="text-lg font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
              {territory ? 'Edit Territory' : 'New Territory'}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors">
            <X className="w-4 h-4" style={{ color: '#64748B' }} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* How-it-works helper */}
          <div
            className="rounded-lg px-3 py-2.5 text-[12px] leading-relaxed flex items-start gap-2"
            style={{
              backgroundColor: 'rgba(26, 54, 101, 0.06)',
              border: '1px solid rgba(26, 54, 101, 0.15)',
              color: '#334155',
            }}
          >
            <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#1A3665' }} />
            <div>
              Draw the shape of this territory on the map. Click to drop vertices;
              click a vertex to remove it. Need a less-precise option? Toggle
              "Use states instead" to pick by state list.
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.1em] mb-1.5 block" style={{ color: '#94A3B8' }}>
              Name <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Ohio River Valley"
              className="w-full h-10 text-sm rounded-lg px-3 focus:outline-none"
              style={{ border: '1px solid #E5E9F0', color: '#0F172A' }}
              onFocusCapture={e => { e.target.style.borderColor = '#1A3665'; e.target.style.boxShadow = '0 0 0 3px rgba(26, 54, 101, 0.1)'; }}
              onBlurCapture={e => { e.target.style.borderColor = '#E5E9F0'; e.target.style.boxShadow = 'none'; }}
              autoFocus
            />
          </div>

          {/* Geography definition — polygon drawing (primary) or state list (fallback) */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#94A3B8' }}>
                Geography <span style={{ color: '#DC2626' }}>*</span>
                {!geoValid && (
                  <span className="ml-2 font-normal normal-case" style={{ color: '#DC2626' }}>
                    · {useStateFallback ? 'pick at least one state' : 'draw at least 3 points'}
                  </span>
                )}
              </label>
              <button
                type="button"
                onClick={() => setUseStateFallback(v => !v)}
                className="text-[11px] hover:underline"
                style={{ color: '#64748B' }}
              >
                {useStateFallback ? 'Draw on map instead' : 'Use states instead'}
              </button>
            </div>

            {useStateFallback ? (
              <>
                <p className="text-[11px] mb-2" style={{ color: '#94A3B8' }}>
                  Click a state to toggle. States that appear in your current pipeline are listed first.
                </p>
                <div className="grid grid-cols-8 gap-1.5">
                  {sortedStates.map(code => {
                    const isActive = states.has(code);
                    const hasDeals = withDealsSet.has(code);
                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={() => toggle(code)}
                        className="h-9 text-xs font-semibold rounded-lg transition-all"
                        style={isActive ? {
                          background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
                          color: 'white',
                          boxShadow: `0 1px 2px ${color}55`,
                        } : {
                          backgroundColor: hasDeals ? 'white' : '#FAFBFD',
                          color: hasDeals ? '#0F172A' : '#94A3B8',
                          border: hasDeals ? '1px solid #E5E9F0' : '1px solid #F0F2F6',
                        }}
                      >
                        {code}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11px] mt-1.5 tabular-nums" style={{ color: states.size === 0 ? '#DC2626' : '#64748B' }}>
                  {states.size} state{states.size === 1 ? '' : 's'} selected
                </div>
              </>
            ) : (
              <TerritoryMapEditor
                value={polygon}
                onChange={setPolygon}
                color={color}
                referencePoints={referencePoints}
              />
            )}
          </div>

          {/* Color */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.1em] mb-1.5 block" style={{ color: '#94A3B8' }}>Color</label>
            <div className="flex gap-2">
              {TERRITORY_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className="w-8 h-8 rounded-lg transition-all"
                  style={{
                    background: `linear-gradient(135deg, ${c} 0%, ${c}CC 100%)`,
                    boxShadow: color === c
                      ? `0 0 0 3px ${c}40, 0 2px 6px ${c}55`
                      : `0 1px 2px ${c}33`,
                    transform: color === c ? 'scale(1.08)' : 'scale(1)',
                  }}
                  title={c}
                />
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.1em] mb-1.5 block" style={{ color: '#94A3B8' }}>Description <span className="font-normal normal-case">(optional)</span></label>
            <input
              type="text" value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Counties along the Ohio River across OH, KY, WV, IN, IL"
              className="w-full h-10 text-sm rounded-lg px-3 focus:outline-none"
              style={{ border: '1px solid #E5E9F0', color: '#0F172A' }}
              onFocusCapture={e => { e.target.style.borderColor = '#1A3665'; e.target.style.boxShadow = '0 0 0 3px rgba(26, 54, 101, 0.1)'; }}
              onBlurCapture={e => { e.target.style.borderColor = '#E5E9F0'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderTop: '1px solid #F0F2F6' }}>
          <div>
            {onDelete && (
              <button
                type="button"
                onClick={onDelete}
                disabled={saving}
                className="h-9 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-lg transition-colors hover:bg-red-50 disabled:opacity-50"
                style={{ color: '#DC2626', border: '1px solid #FEE2E2' }}
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="h-9 inline-flex items-center gap-1.5 px-4 text-xs font-medium rounded-lg transition-colors hover:bg-slate-50 disabled:opacity-50"
              style={{ backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !name.trim() || !geoValid}
              className="h-9 inline-flex items-center gap-1.5 px-4 text-xs font-semibold rounded-lg text-white transition-all hover:shadow-md disabled:opacity-40"
              style={{
                background: `linear-gradient(135deg, ${pc} 0%, #2E4B7C 100%)`,
                boxShadow: `0 1px 2px ${pc}40`,
              }}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {territory ? 'Save changes' : 'Create territory'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PORTFOLIO VIEW COMPONENT
// ============================================================================
function PortfolioView({ organization, userProfile, pc, sc, accent, isViewer, isLimitedViewer, isDealAdvisor, isAdmin, isBoardMember, isIntegration, setView, setActiveDealId, showModal, setShowModal, newDealName, setNewDealName, newMember, setNewMember, signOut, showToast }) {
  const { darkMode, setDarkMode } = useTheme();
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(pc, sc);
  const { deals, loading, createDeal, deleteDeal, setNotifyOnUpload } = useDeals(organization?.id, isDealAdvisor ? { id: userProfile?.id, role: 'deal_advisor' } : null);
  const { team, inviteMember } = useTeam(organization?.id);
  const { stages: dbStages } = useDealStages(organization?.id);
  const { delayCategories } = useDealReasonCategories(organization?.id);
  const { locations: lfgLocations } = useLfgLocations(organization?.id);
  const [dealDistances, setDealDistances] = useState({});
  // Phase C.5 — territory/state filter data. dealStates maps dealId → Set of
  // state codes (a deal can span multiple states). dealLocations carries
  // the full per-location records (state + lat/lng) so we can do
  // point-in-polygon tests for drawn territories.
  const [dealStates, setDealStates] = useState({});
  const [dealLocations, setDealLocations] = useState({});
  const [selectedStates, setSelectedStates] = useState(new Set());
  // Phase C.5.2 — territories
  const { territories, createTerritory, updateTerritory, deleteTerritory } = useTerritories(organization?.id);
  const [selectedTerritoryIds, setSelectedTerritoryIds] = useState(new Set());
  const [editingTerritory, setEditingTerritory] = useState(null); // null | {...} for edit or 'new'
  // Phase C.5.3 — stage filter
  const [selectedStages, setSelectedStages] = useState(new Set());
  // Phase R.2.8 — priority chip filter (mirrors stage chips). 'unset' picks
  // deals with no priority field set; the others are critical/high/medium/low.
  const [selectedPriorities, setSelectedPriorities] = useState(new Set());
  // Phase R.2.2 — free-text search across deal names + cluster + primary contact.
  // Matches across every section, including collapsed ones — and auto-expands
  // collapsed sections when the search matches something inside them.
  const [dealSearch, setDealSearch] = useState('');
  // Phase R.2.3 — also surface soft-deleted deals under search so users can
  // find/restore a deal they previously trashed. The deal_summary view
  // excludes deleted_at IS NOT NULL, so we pull these directly from the
  // base table once on mount.
  const [deletedDeals, setDeletedDeals] = useState([]);
  // Phase R.2 — sort order for deals within each section. Persists in
  // localStorage so it survives page reloads. Defaults to priority so the
  // hot deals float to the top of every section.
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem('lfg.portfolio.sortBy') || 'priority'; } catch { return 'priority'; }
  });
  useEffect(() => {
    try { localStorage.setItem('lfg.portfolio.sortBy', sortBy); } catch { /* ignore */ }
  }, [sortBy]);
  // Custom drag-sort order — persisted per section in localStorage.
  const [customOrder, setCustomOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem('lfg.portfolio.customOrder') || 'null') || {}; }
    catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem('lfg.portfolio.customOrder', JSON.stringify(customOrder)); } catch {}
  }, [customOrder]);
  // Track which deal is currently being dragged (for DragOverlay).
  const [activeDragId, setActiveDragId] = useState(null);
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );
  // Phase R.2.1 — view mode: 'cards' (rich tile, ~120px/deal) vs
  // 'compact' (~36px row, fits 20+ deals on screen). Default to compact
  // once the portfolio has more than 12 deals — Alfie's concern about
  // wasted real estate when there are many deals.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('lfg.portfolio.viewMode') || 'compact'; } catch { return 'compact'; }
  });
  useEffect(() => {
    try { localStorage.setItem('lfg.portfolio.viewMode', viewMode); } catch { /* ignore */ }
  }, [viewMode]);
  // Persisted per-section collapse state, same pattern as BriefingsPanel's
  // `lfg.radar.collapsed` — Prospects now starts collapsed too (usually
  // the largest, least-urgent section) and remembers whatever the user
  // last set, instead of resetting to expanded every reload.
  const SECTION_COLLAPSE_DEFAULTS = { active: false, prospects: true, onHold: true, canceled: true, complete: true, integration: false };
  const [collapsedSections, setCollapsedSections] = useState(() => {
    const initial = {};
    Object.keys(SECTION_COLLAPSE_DEFAULTS).forEach(key => {
      try {
        const stored = localStorage.getItem(`lfg.portfolio.collapsed.${key}`);
        initial[key] = stored !== null ? stored === 'true' : SECTION_COLLAPSE_DEFAULTS[key];
      } catch {
        initial[key] = SECTION_COLLAPSE_DEFAULTS[key];
      }
    });
    return initial;
  });

  // AI Deal from Files state
  const [aiDealFiles, setAiDealFiles] = useState([]);
  const [aiDealStep, setAiDealStep] = useState(null); // null | 'uploading' | 'extracting' | 'confirm' | 'creating'
  const [aiDealInfo, setAiDealInfo] = useState({ business_name: '', primary_contact: '', primary_email: '', primary_phone: '', broker_name: '', broker_firm: '', broker_email: '', broker_phone: '', locations: [], notes: '' });
  const aiDealFileRef = React.useRef(null);
  const [aiDealDragActive, setAiDealDragActive] = useState(false);

  // Haversine distance calculation (miles)
  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // Compute nearest LFG distance for each deal
  useEffect(() => {
    if (!deals.length || !lfgLocations.length || !organization?.id) return;
    
    const lfgWithCoords = lfgLocations.filter(l => l.latitude && l.longitude);
    if (!lfgWithCoords.length) return;

    // Fetch all deal_locations for non-complete deals
    const fetchDistances = async () => {
      try {
        const nonCompleteDeals = deals.filter(d => !['complete', 'dead', 'canceled'].includes(d.stage));
        if (!nonCompleteDeals.length) return;

        const { data: allDealLocs, error } = await supabase
          .from('deal_locations')
          .select('deal_id, latitude, longitude')
          .in('deal_id', nonCompleteDeals.map(d => d.id))
          // Phase C.6 — unconfirmed AI proposals shouldn't skew "nearest LFG" distance.
          .eq('is_confirmed', true);

        if (error) { console.error('Error fetching deal locations:', error); return; }
        if (!allDealLocs?.length) return;

        const distances = {};
        nonCompleteDeals.forEach(deal => {
          const locs = allDealLocs.filter(dl => dl.deal_id === deal.id && dl.latitude && dl.longitude);
          if (!locs.length) return;

          let minDist = Infinity;
          locs.forEach(dl => {
            lfgWithCoords.forEach(lfg => {
              const d = haversineDistance(dl.latitude, dl.longitude, lfg.latitude, lfg.longitude);
              if (d < minDist) minDist = d;
            });
          });

          if (minDist < Infinity) {
            distances[deal.id] = Math.round(minDist);
          }
        });

        setDealDistances(distances);
      } catch (err) {
        console.error('Error computing distances:', err);
      }
    };

    fetchDistances();
  }, [deals, lfgLocations, organization?.id]);

  // Phase C.5 — load deal locations for territory filtering. Builds two maps:
  //   • dealStates: dealId → Set of state codes (for state filters)
  //   • dealLocations: dealId → array of { state, lat, lng } (for polygon tests)
  useEffect(() => {
    if (!deals.length || !organization?.id) { setDealStates({}); setDealLocations({}); return; }
    const fetchLocs = async () => {
      try {
        const { data, error } = await supabase
          .from('deal_locations')
          .select('deal_id, state, latitude, longitude')
          .in('deal_id', deals.map(d => d.id))
          // Phase C.6 — unconfirmed AI proposals shouldn't affect geo-filtering.
          .eq('is_confirmed', true);
        if (error) { console.error('Error fetching deal locations:', error); return; }
        const states = {};
        const locs = {};
        (data || []).forEach(loc => {
          if (!locs[loc.deal_id]) locs[loc.deal_id] = [];
          locs[loc.deal_id].push({
            state: loc.state ? String(loc.state).toUpperCase().trim() : null,
            lat: loc.latitude != null ? Number(loc.latitude) : null,
            lng: loc.longitude != null ? Number(loc.longitude) : null,
          });
          if (loc.state) {
            const st = String(loc.state).toUpperCase().trim();
            if (st) {
              if (!states[loc.deal_id]) states[loc.deal_id] = new Set();
              states[loc.deal_id].add(st);
            }
          }
        });
        setDealStates(states);
        setDealLocations(locs);
      } catch (err) { console.error('fetchLocs error:', err); }
    };
    fetchLocs();
  }, [deals, organization?.id]);

  // All states represented across the pipeline, with per-state deal counts.
  const availableStates = useMemo(() => {
    const counts = {};
    deals.forEach(d => {
      const s = dealStates[d.id];
      if (!s) return;
      s.forEach(st => { counts[st] = (counts[st] || 0) + 1; });
    });
    return Object.entries(counts).map(([code, count]) => ({ code, count })).sort((a, b) => a.code.localeCompare(b.code));
  }, [deals, dealStates]);

  // Effective geographic filter — a set of states AND a set of GeoJSON
  // polygons pulled from explicit chips + selected territories. A deal
  // matches when any of its locations is in one of the states OR inside
  // one of the polygons. null = no geo filter active.
  const effectiveGeoFilter = useMemo(() => {
    if (selectedStates.size === 0 && selectedTerritoryIds.size === 0) return null;
    const states = new Set(selectedStates);
    const polygons = [];
    territories.forEach(t => {
      if (!selectedTerritoryIds.has(t.id)) return;
      (t.states || []).forEach(s => states.add(String(s).toUpperCase()));
      if (t.drawn_geometry) polygons.push(t.drawn_geometry);
    });
    if (states.size === 0 && polygons.length === 0) return null;
    return { states, polygons };
  }, [selectedStates, selectedTerritoryIds, territories]);

  // Phase R.2.2 — title-case helper for display. We do NOT mutate the
  // underlying name (search/edits use the raw value); only the rendered
  // text changes. Per-word capitalization gives "HENNEY FAMILY SERVICES,
  // INC." → "Henney Family Services, Inc." Short all-caps tokens
  // ("RR FH", "LLC") will read as "Rr Fh" / "Llc" — Alfie accepted that
  // tradeoff for consistency across the whole list.
  const titleCase = (s) => {
    if (!s) return s;
    return String(s).toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  };

  // Compound filter: geo (state ∪ polygon) AND stage AND search.
  // Dimensions AND; chips within a dimension OR. None active → show
  // everything.
  const filteredDeals = useMemo(() => {
    const geoActive = !!effectiveGeoFilter;
    const stageActive = selectedStages.size > 0;
    const priorityActive = selectedPriorities.size > 0;
    const searchActive = !!dealSearch.trim();
    if (!geoActive && !stageActive && !priorityActive && !searchActive) return deals;
    const q = dealSearch.trim().toLowerCase();
    return deals.filter(d => {
      if (geoActive) {
        const locs = dealLocations[d.id] || [];
        let geoMatch = false;
        for (const loc of locs) {
          // state match
          if (loc.state && effectiveGeoFilter.states.has(loc.state)) { geoMatch = true; break; }
          // polygon match (needs coords)
          if (loc.lat != null && loc.lng != null) {
            for (const poly of effectiveGeoFilter.polygons) {
              if (pointInPolygon(loc.lat, loc.lng, poly)) { geoMatch = true; break; }
            }
          }
          if (geoMatch) break;
        }
        if (!geoMatch) return false;
      }
      if (stageActive && !selectedStages.has(d.stage)) return false;
      if (priorityActive && !selectedPriorities.has(d.priority || 'unset')) return false;
      if (searchActive) {
        const hay = [d.name, d.cluster, d.primary_contact, d.primary_email, d.city, d.state]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [deals, dealLocations, effectiveGeoFilter, selectedStages, selectedPriorities, dealSearch]);

  const anyFilterActive = !!effectiveGeoFilter || selectedStages.size > 0 || selectedPriorities.size > 0 || !!dealSearch.trim();

  const toggleState = (code) => {
    setSelectedStates(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };
  const toggleTerritory = (id) => {
    setSelectedTerritoryIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleStage = (id) => {
    setSelectedStages(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const togglePriority = (id) => {
    setSelectedPriorities(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearAllFilters = () => {
    setSelectedStates(new Set());
    setSelectedTerritoryIds(new Set());
    setSelectedStages(new Set());
    setSelectedPriorities(new Set());
    setDealSearch('');
  };

  // Use database stages or fallback to defaults
  const PHASES = dbStages?.length > 0 ? dbStages : DEFAULT_PHASES;
  
  // Group deals into 4 categories (respecting state filter)
  // Stages that mean "deal closed but integration is still in progress".
  // Both 'close' and 'closed' kept because real deals use both spellings
  // (the canonical seed has 'closed' but production data has 'close').
  const INTEGRATION_STAGES = useMemo(() => new Set([
    'close', 'closed',
    'integration-1-30', 'integration-31-60', 'integration-61-90',
  ]), []);

  // Phase R.2 — sort comparator. Higher-priority deals first by default,
  // ties broken by most-recently-updated. Other sort modes are
  // self-explanatory (name, target close date, distance, revenue).
  const sortDealsList = (list) => {
    const PRI = { critical: 4, high: 3, medium: 2, low: 1 };
    const compare = {
      priority: (a, b) => {
        const ap = PRI[a.priority] || 0;
        const bp = PRI[b.priority] || 0;
        if (ap !== bp) return bp - ap;
        return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
      },
      name: (a, b) => (a.name || '').localeCompare(b.name || ''),
      updated: (a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at),
      target_close: (a, b) => {
        const av = a.target_close_date ? new Date(a.target_close_date) : Infinity;
        const bv = b.target_close_date ? new Date(b.target_close_date) : Infinity;
        return av - bv;
      },
      distance: (a, b) => {
        const ad = dealDistances[a.id];
        const bd = dealDistances[b.id];
        if (ad == null && bd == null) return 0;
        if (ad == null) return 1;
        if (bd == null) return -1;
        return ad - bd;
      },
      revenue: (a, b) => (b.annual_revenue || 0) - (a.annual_revenue || 0),
      created: (a, b) => new Date(b.created_at) - new Date(a.created_at),
      cluster: (a, b) => (a.cluster || 'zzz').localeCompare(b.cluster || 'zzz'),
    };
    const fn = compare[sortBy] || compare.priority;
    return [...list].sort(fn);
  };

  // Saved custom-order arrays are a one-time snapshot (see handleSortChange
  // below) — any deal added to a section afterward isn't in the array. This
  // tops it up with those missing IDs (appended in current display order,
  // same "unknown IDs go last" rule applyCustomOrder already uses for
  // rendering) so drag-and-drop indexOf lookups resolve for newly-added
  // deals instead of silently no-op'ing. Without this, sections keep working
  // for only the deals that existed when custom sort was first turned on.
  const getFullOrder = (sectionKey, deals) => {
    const saved = customOrder[sectionKey] || [];
    const savedSet = new Set(saved);
    const extras = deals.map(d => d.id).filter(id => !savedSet.has(id));
    return [...saved, ...extras];
  };

  // When sortBy==='custom', order by the saved ID array; unknown IDs go last.
  const applyCustomOrder = (list, sectionKey) => {
    if (sortBy !== 'custom') return sortDealsList(list);
    const order = customOrder[sectionKey] || [];
    return [...list].sort((a, b) => {
      const ai = order.indexOf(a.id);
      const bi = order.indexOf(b.id);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  };

  // Switching to 'custom' snapshots the current sort order as the starting
  // point so the list doesn't jump on first activation.
  const handleSortChange = (newSort) => {
    if (newSort === 'custom' && sortBy !== 'custom') {
      const init = {};
      ['active','prospects','onHold','integration','canceled','complete'].forEach(key => {
        if (categorizedDeals[key]?.length) {
          init[key] = sortDealsList(categorizedDeals[key]).map(d => d.id);
        }
      });
      setCustomOrder(prev => ({ ...prev, ...init }));
    }
    setSortBy(newSort);
  };

  const categorizedDeals = useMemo(() => {
    const prospects = [];
    const active = [];
    const onHold = [];
    const integration = [];
    const canceled = [];
    const complete = [];

    filteredDeals.forEach(deal => {
      const stage = PHASES.find(p => p.id === deal.stage);

      // Staleness tier for the inline status dot on Active rows — display
      // only. A stale deal no longer gets demoted out of Active into a
      // separate "Stalled" bucket; it stays put with a color + reason
      // instead, per Ray: "this way they stay Active or I move them to
      // on hold."
      const lastUpdate = new Date(deal.updated_at || deal.created_at);
      const daysSinceUpdate = Math.floor((Date.now() - lastUpdate) / (1000 * 60 * 60 * 24));
      const staleTier = daysSinceUpdate >= 14 ? 'red' : daysSinceUpdate >= 7 ? 'yellow' : 'green';

      if (deal.stage === 'canceled' || deal.stage === 'dead') {
        canceled.push(deal);
      } else if (deal.stage === 'complete') {
        complete.push(deal);
      } else if (INTEGRATION_STAGES.has(deal.stage)) {
        integration.push(deal);
      } else if (stage?.is_terminal) {
        complete.push(deal);
      } else if (deal.stage === 'on-hold') {
        // On Hold reads as inactive — group it with the dead deals, not
        // Active, so Active stays a true "currently being worked" list.
        onHold.push(deal);
      } else if (deal.stage === 'prospect') {
        prospects.push(deal);
      } else {
        active.push({ ...deal, daysSinceUpdate, staleTier });
      }
    });

    return { prospects, active, onHold, integration, canceled, complete };
  }, [filteredDeals, PHASES, INTEGRATION_STAGES]);

  const toggleSection = (section) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [section]: !prev[section] };
      try { localStorage.setItem(`lfg.portfolio.collapsed.${section}`, String(next[section])); } catch { /* ignore */ }
      return next;
    });
  };

  // Phase R.2.2 — when the user is actively searching, force every section
  // open so matches in normally-collapsed buckets (canceled, complete) are
  // visible without an extra click.
  const isSearching = !!dealSearch.trim();

  // Phase R.2.3 — load soft-deleted deals from base table (deal_summary
  // filters them out). Refreshes when a delete happens via the realtime
  // channel below. Limited to 500 — should be plenty since deletes
  // are rare.
  useEffect(() => {
    // deal_advisor is scoped to 1-2 specific deals via deal_access — this
    // fetch pulls org-wide deleted deals (including purchase price/EBITDA)
    // into memory with no deal_access filtering at all, only surfaced when
    // a search matches. Skip it entirely for that role rather than filter
    // after the fact, so the data never lands in client memory to begin with.
    if (!organization?.id || isDealAdvisor) return;
    let cancelled = false;
    const fetchDeleted = async () => {
      const { data, error } = await supabase
        .from('deals')
        .select('id, name, stage, cluster, priority, primary_contact, primary_email, city, state, asking_price, ebitda, annual_revenue, annual_calls, purchase_price, deleted_at, created_by')
        .eq('organization_id', organization.id)
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false })
        .limit(500);
      if (cancelled) return;
      if (!error) setDeletedDeals(data || []);
    };
    fetchDeleted();
    const channel = supabase
      .channel(`deleted-deals-${organization.id}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'deals', filter: `organization_id=eq.${organization.id}` },
        () => fetchDeleted()
      )
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(channel); };
  }, [organization?.id, isDealAdvisor]);

  // Matching deleted deals — only computed when search is active so we
  // don't surface a "Deleted" section unprompted.
  const deletedMatches = useMemo(() => {
    if (!isSearching) return [];
    const q = dealSearch.toLowerCase().trim();
    return deletedDeals.filter(d => {
      const hay = [d.name, d.cluster, d.primary_contact, d.primary_email, d.city, d.state]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [isSearching, dealSearch, deletedDeals]);

  // Restore a soft-deleted deal — clears deleted_at. Realtime channels in
  // useDeals + deletedDeals fetcher both pick this up.
  const handleRestoreDeal = async (e, dealId, dealName) => {
    e.stopPropagation();
    if (!window.confirm(`Restore "${titleCase(dealName)}"? It will reappear in the pipeline.`)) return;
    try {
      const { error } = await supabase
        .from('deals')
        .update({ deleted_at: null, updated_at: new Date().toISOString() })
        .eq('id', dealId);
      if (error) throw error;
      showToast(`Restored ${titleCase(dealName)}`);
    } catch (err) {
      showToast(err.message || 'Failed to restore', 'error');
    }
  };

  // deal_advisor is scoped to 1-2 specific deals via deal_access — breaking
  // those out into Active/Prospects/Stalled/Integration/etc. buckets (the
  // internal pipeline-stage taxonomy) is both meaningless at that scale and
  // exposes stage-category structure this role shouldn't see. One flat
  // section instead, reusing the exact same section-rendering code path.
  const sections = isDealAdvisor
    ? [{ key: 'deals', label: 'Your Deals', deals: applyCustomOrder(filteredDeals, 'deals'), color: displayPrimary, emptyText: 'No deals yet.' }]
    : [
        { key: 'active', label: 'Active Deals', deals: applyCustomOrder(categorizedDeals.active, 'active'), color: displayPrimary, emptyText: 'No active deals yet — once a prospect is engaged (Pre-LOI onwards), it lands here.' },
        { key: 'prospects', label: 'Prospects', deals: applyCustomOrder(categorizedDeals.prospects, 'prospects'), color: '#64748B', emptyText: null },
        { key: 'integration', label: 'Integration', deals: applyCustomOrder(categorizedDeals.integration, 'integration'), color: '#0EA5E9', emptyText: null },
        // On Hold groups with the dead deals, not Active — Ray: "On Hold
        // should probably be shown with the Dead deals, not in Active deals."
        { key: 'onHold', label: 'On Hold', deals: applyCustomOrder(categorizedDeals.onHold, 'onHold'), color: '#A855F7', emptyText: null },
        { key: 'canceled', label: 'Canceled Deals', deals: applyCustomOrder(categorizedDeals.canceled, 'canceled'), color: '#ef4444', emptyText: null },
        { key: 'complete', label: 'Complete Deals', deals: applyCustomOrder(categorizedDeals.complete, 'complete'), color: '#10b981', emptyText: null },
        // Phase R.2.3 — Deleted matches under search. Only renders when the
        // search is active AND it matches at least one deleted deal. Each
        // row shows a "Deleted" badge and a restore button.
        ...(deletedMatches.length > 0 ? [{
          key: 'deleted', label: 'Deleted (search results)', deals: deletedMatches,
          color: '#64748B', emptyText: null, isDeleted: true,
        }] : []),
      ];

  const handleCreateDeal = async () => {
    if (!newDealName.trim()) return;
    try {
      const sanitized = sanitizeDealName(newDealName);
      if (sanitized.changed) {
        showToast(`Slashes aren't allowed in deal names (they break SharePoint). Saved as "${sanitized.name}".`);
      }
      const deal = await createDeal(sanitized.name);
      setNewDealName('');
      setShowModal(null);
      setActiveDealId(deal.id);
      setView('deal');
      showToast('Deal created — AI is researching this business…');

      // Background AI enrichment — finds locations, owner, calls, etc.
      // Fire-and-forget; user can keep working while it runs.
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-enrich-deal`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ deal_id: deal.id }),
          }).catch(e => console.warn('ai-enrich-deal background:', e.message));
        }
      } catch (e) { /* enrichment failure shouldn't block deal creation */ }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // AI Deal from Files — Step 0: APPEND a batch of files to the staging
  // list. Lets the user collect files from multiple sources (desktop pick,
  // drag-drop, paste from Outlook, etc.) before running AI extraction.
  // Dedupes by name+size so re-dropping the same file doesn't double-count.
  const handleAiDealFilesAdd = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setAiDealFiles(prev => {
      const seen = new Set(prev.map(f => `${f.name}::${f.size}`));
      const additions = incoming.filter(f => !seen.has(`${f.name}::${f.size}`));
      return [...prev, ...additions];
    });
  };

  const handleAiDealFileRemove = (idx) => {
    setAiDealFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // AI Deal from Files — Step 1: kick off AI extraction on the currently-
  // staged file list. User clicks "Continue" once they're done staging.
  const handleAiDealStartExtraction = async () => {
    const files = aiDealFiles;
    if (!files.length) return;
    setAiDealStep('extracting');

    try {
      // Pick the best files for extraction — prefer PDFs with business-relevant names
      const priorityTerms = ['memorandum', 'overview', 'summary', 'asset', 'listing', 'business', 'sales', 'profile', 'offering'];
      const pdfFiles = files.filter(f => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
      const scoredFiles = (pdfFiles.length > 0 ? pdfFiles : files).map(f => {
        const nameLower = f.name.toLowerCase();
        const score = priorityTerms.reduce((s, term) => s + (nameLower.includes(term) ? 10 : 0), 0)
          + (f.type === 'application/pdf' ? 5 : 0)
          + (f.size > 50000 ? 2 : 0);
        return { file: f, score };
      });
      scoredFiles.sort((a, b) => b.score - a.score);
      
      // Send top 2 files for richer extraction
      const topFiles = scoredFiles.slice(0, Math.min(2, scoredFiles.length));
      console.log(`AI extraction: selected ${topFiles.map(f => `"${f.file.name}" (${f.score})`).join(', ')} from ${files.length} files`);

      const filePayloads = [];
      for (const { file: f } of topFiles) {
        const ab = await f.arrayBuffer();
        const b64 = btoa(new Uint8Array(ab).reduce((d, b) => d + String.fromCharCode(b), ''));
        filePayloads.push({ file_base64: b64, file_name: f.name, mime_type: f.type });
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-extract-deal-info`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            files: filePayloads,
            // Backwards compat
            file_base64: filePayloads[0].file_base64,
            file_name: filePayloads[0].file_name,
            mime_type: filePayloads[0].mime_type,
          })
        }
      );

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'AI extraction failed');

      // Support both new "locations" array and old address+additional_locations format
      let locations = result.locations || [];
      if (locations.length === 0) {
        if (result.address || result.city) {
          locations.push({
            name: result.business_name || '',
            address: result.address || '',
            city: result.city || '',
            state: result.state || '',
            zip: result.zip || '',
          });
        }
        if (result.additional_locations?.length > 0) {
          locations.push(...result.additional_locations);
        }
      }

      setAiDealInfo({
        business_name: result.business_name || '',
        primary_contact: result.primary_contact || '',
        primary_email: result.primary_email || '',
        primary_phone: result.primary_phone || '',
        broker_name: result.broker_name || '',
        broker_firm: result.broker_firm || '',
        broker_email: result.broker_email || '',
        broker_phone: result.broker_phone || '',
        locations: locations,
        notes: result.notes || '',
      });
      setAiDealStep('confirm');
    } catch (err) {
      console.error('AI extraction error:', err);
      showToast('AI extraction failed: ' + err.message, 'error');
      // Fall back to manual entry with file names
      const guessName = files[0].name.replace(/\.(pdf|docx?|xlsx?|csv|png|jpg)$/i, '').replace(/[_-]/g, ' ');
      setAiDealInfo(prev => ({ ...prev, business_name: guessName }));
      setAiDealStep('confirm');
    }
  };

  // AI Deal from Files — Step 2: User confirms, deal created, files processed
  const handleAiDealConfirm = async () => {
    if (!aiDealInfo.business_name.trim()) {
      showToast('Business name is required', 'error');
      return;
    }
    setAiDealStep('creating');
    const spUrl = import.meta.env.VITE_SUPABASE_URL;
    const spKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    try {
      // 1. Create the deal — sanitize name so SharePoint folder paths
      // don't fragment on slashes.
      const sanitized = sanitizeDealName(aiDealInfo.business_name);
      if (sanitized.changed) {
        showToast(`Slashes removed from name (SharePoint compat). Saved as "${sanitized.name}".`);
      }
      const deal = await createDeal(sanitized.name);
      const dealId = deal.id;

      // 2. Update deal with extracted contact/broker info (no address — all locations go to deal_locations)
      const updates = {};
      if (aiDealInfo.primary_contact) updates.primary_contact = aiDealInfo.primary_contact;
      if (aiDealInfo.primary_email) updates.primary_email = aiDealInfo.primary_email;
      if (aiDealInfo.primary_phone) updates.primary_phone = aiDealInfo.primary_phone;
      if (aiDealInfo.broker_name) updates.broker_name = aiDealInfo.broker_name;
      if (aiDealInfo.broker_firm) updates.broker_firm = aiDealInfo.broker_firm;
      if (aiDealInfo.broker_email) updates.broker_email = aiDealInfo.broker_email;
      if (aiDealInfo.broker_phone) updates.broker_phone = aiDealInfo.broker_phone;

      if (Object.keys(updates).length > 0) {
        await supabase.from('deals').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', dealId);
      }

      // 2b. Create all locations in deal_locations immediately (coords null), geocode in background
      const locationsToCreate = (aiDealInfo.locations || []).filter(l => l.name || l.address || l.city);
      // If no locations extracted, create a placeholder using the business name
      if (locationsToCreate.length === 0) {
        locationsToCreate.push({ name: aiDealInfo.business_name.trim(), address: '', city: '', state: '', zip: '' });
      }

      const insertedLocations = [];
      for (const loc of locationsToCreate) {
        try {
          const { data: inserted } = await supabase.from('deal_locations').insert({
            deal_id: dealId,
            organization_id: organization.id,
            name: loc.name || aiDealInfo.business_name.trim(),
            address: loc.address || null,
            city: loc.city || null,
            state: loc.state ? loc.state.toUpperCase() : null,
            zip: loc.zip || null,
          }).select().single();
          if (inserted) insertedLocations.push(inserted);
        } catch (locErr) { console.error('Location insert error:', locErr); }
      }

      // 3. Set initial AI summary with total expected count so DealView shows progress
      const filesToProcess = [...aiDealFiles];
      const fileCount = filesToProcess.length;
      const dealNameForToast = sanitized.name;

      await supabase.from('deals').update({
        ai_summary: { documents_processed: 0, total_expected: filesToProcess.length, documents: [], narrative: `Uploading ${filesToProcess.length} files...` },
        updated_at: new Date().toISOString(),
      }).eq('id', dealId);

      // 4. Navigate to the new deal immediately
      setShowModal(null);
      setAiDealStep(null);
      setAiDealFiles([]);
      setAiDealInfo({ business_name: '', primary_contact: '', primary_email: '', primary_phone: '', broker_name: '', broker_firm: '', broker_email: '', broker_phone: '', locations: [], notes: '' });
      setActiveDealId(dealId);
      setView('deal');
      showToast(`Deal "${dealNameForToast}" created — ${fileCount} files processing`);

      // 5. Upload files in the background (non-blocking)
      (async () => {
        // Create SharePoint folder structure first
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            await fetch(`${spUrl}/functions/v1/sharepoint-sync`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}`, 'apikey': spKey },
              body: JSON.stringify({ action: 'create-deal-structure', dealName: dealNameForToast, organizationId: organization.id }),
            });
            console.log('SharePoint deal structure created');
          }
        } catch (spErr) { console.error('SharePoint structure creation failed:', spErr); }

        // Geocode locations in the background (Nominatim is 1 req/sec)
        (async () => {
          for (let i = 0; i < insertedLocations.length; i++) {
            const loc = insertedLocations[i];
            if (!loc.address && !loc.city) continue;
            try {
              const parts = [loc.address, loc.city, loc.state, loc.zip].filter(Boolean).join(', ');
              const resp = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(parts + ', USA')}&limit=1`,
                { headers: { 'User-Agent': 'LFG-Acquisitions-Portal' } }
              );
              const data = await resp.json();
              let lat = null, lng = null;
              if (data?.length) {
                lat = parseFloat(data[0].lat); lng = parseFloat(data[0].lon);
              } else if (loc.city && loc.state) {
                // Fallback to city/state
                const resp2 = await fetch(
                  `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(`${loc.city}, ${loc.state} ${loc.zip || ''}, USA`)}&limit=1`,
                  { headers: { 'User-Agent': 'LFG-Acquisitions-Portal' } }
                );
                const data2 = await resp2.json();
                if (data2?.length) { lat = parseFloat(data2[0].lat); lng = parseFloat(data2[0].lon); }
              }
              if (lat && lng) {
                await supabase.from('deal_locations').update({ latitude: lat, longitude: lng }).eq('id', loc.id);
                console.log(`Geocoded "${loc.name}": ${lat}, ${lng}`);
              }
            } catch (e) { console.error('Background geocode error:', e); }
            // Respect Nominatim rate limit
            if (i < insertedLocations.length - 1) await new Promise(r => setTimeout(r, 1100));
          }
        })();

        let uploaded = 0;
        for (const file of filesToProcess) {
          try {
            const fileExt = file.name.split('.').pop();
            const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
            const storagePath = `seller-uploads/${dealId}/general/${fileName}`;

            const { error: storageErr } = await supabase.storage
              .from('deal-documents')
              .upload(storagePath, file, { cacheControl: '3600', upsert: false });
            if (storageErr) { console.error('Storage upload error:', storageErr); continue; }

            const { data: fileRecord, error: dbErr } = await supabase.from('files').insert({
              deal_id: dealId,
              organization_id: organization.id,
              name: fileName,
              original_name: file.name,
              storage_path: storagePath,
              size_bytes: file.size,
              mime_type: file.type,
              uploaded_by_id: userProfile?.id,
              uploaded_by_name: userProfile?.full_name || 'Unknown',
              uploaded_by_type: 'user',
            }).select().single();

            if (dbErr) { console.error('DB insert error:', dbErr); continue; }

            // Fire auto-categorize (non-blocking)
            fetch(`${spUrl}/functions/v1/auto-categorize-document`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'apikey': spKey },
              body: JSON.stringify({ file_id: fileRecord.id, deal_id: dealId }),
            })
              .then(r => r.json())
              .then(r => console.log(`AI categorized ${file.name}:`, r))
              .catch(err => console.error(`AI categorize failed for ${file.name}:`, err));

            // Phase C — also try to match against DD checklist items (any assignee)
            fetch(`${spUrl}/functions/v1/ai-match-dd-item`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'apikey': spKey },
              body: JSON.stringify({ file_id: fileRecord.id, deal_id: dealId }),
            })
              .then(r => r.json())
              .then(r => console.log(`DD match for ${file.name}:`, r))
              .catch(err => console.error(`DD match failed for ${file.name}:`, err));
          } catch (fileErr) {
            console.error(`Error processing ${file.name}:`, fileErr);
          }
        }
        console.log(`Background upload complete: ${fileCount} files processed`);
      })();
    } catch (err) {
      console.error('Deal creation error:', err);
      showToast(err.message, 'error');
      setAiDealStep('confirm');
    }
  };

  const handleDeleteDeal = async (e, dealId, dealName) => {
    e.stopPropagation(); // Prevent clicking into the deal
    if (!confirm(`Delete "${dealName}"? This will also delete all tasks and files associated with this deal. This cannot be undone.`)) return;
    try {
      await deleteDeal(dealId);
      showToast('Deal deleted');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleExportDeals = () => {
    // Build CSV content
    const headers = ['Name', 'Stage', 'Contact', 'Email', 'Phone', 'Address', 'City', 'State', 'ZIP', 'LOI Date', 'Target Close', 'Tasks Complete', 'Total Tasks', 'Docs Requested', 'Created'];
    
    const rows = deals.map(deal => {
      const phase = PHASES.find(p => p.id === deal.stage);
      return [
        deal.name || '',
        phase?.name || deal.stage || '',
        deal.primary_contact || '',
        deal.primary_email || '',
        deal.primary_phone || '',
        deal.address || '',
        deal.city || '',
        deal.state || '',
        deal.zip || '',
        deal.loi_date || '',
        deal.target_close_date || '',
        deal.completed_tasks || 0,
        deal.total_tasks || 0,
        deal.requested_tasks || 0,
        deal.created_at ? new Date(deal.created_at).toLocaleDateString() : ''
      ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deals-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    showToast('Deals exported to CSV');
  };

  // Alfie: comparing LFG's footprint against deal/prospect locations to
  // pick a banking partner whose branch network matches both. One CSV,
  // one row per location (LFG chapel or deal), so it can go straight to
  // the bank without hand-merging two exports.
  const csvCell = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;

  const handleExportFootprintCsv = async () => {
    try {
      const [lfgRes, dealLocRes] = await Promise.all([
        supabase
          .from('lfg_locations')
          .select('name, address, city, state, zip')
          .eq('organization_id', organization?.id)
          .eq('is_active', true)
          .range(0, 4999),
        // deals.address/city/state/zip are almost always blank — the real
        // per-chapel addresses that actually drive the map pins live here
        // instead. A deal can have more than one (multi-chapel groups), so
        // this is one CSV row per physical location, not per deal.
        supabase
          .from('deal_locations')
          .select('name, address, city, state, zip, deals!inner(name, stage, deleted_at)')
          .eq('organization_id', organization?.id)
          .is('deals.deleted_at', null)
          // Phase C.6 — this export goes to an external banking partner;
          // unconfirmed AI-proposed addresses must never leave the app.
          .eq('is_confirmed', true)
          .range(0, 4999),
      ]);
      if (lfgRes.error) throw lfgRes.error;
      if (dealLocRes.error) throw dealLocRes.error;

      const headers = ['Deal', 'Location Name', 'Type', 'Address', 'City', 'State', 'ZIP', 'Deal Stage'];
      const lfgRows = (lfgRes.data || []).map(l => ['', titleCaseIfShouting(l.name), 'LFG Location', titleCaseIfShouting(l.address), titleCaseIfShouting(l.city), l.state, l.zip, '']);
      const dealRows = (dealLocRes.data || []).map(l => {
        const phase = PHASES.find(p => p.id === l.deals?.stage);
        return [l.deals?.name, titleCaseIfShouting(l.name), 'Deal Location', titleCaseIfShouting(l.address), titleCaseIfShouting(l.city), l.state, l.zip, phase?.name || l.deals?.stage];
      });
      const csv = [headers, ...lfgRows, ...dealRows]
        .map(row => row.map(csvCell).join(','))
        .join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `footprint-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast(`Footprint exported — ${lfgRows.length} LFG location(s), ${dealRows.length} deal location(s)`);
    } catch (err) {
      showToast('Footprint export failed: ' + err.message, 'error');
    }
  };

  // Weekly pipeline reports — per-task detail, not just the deal-level
  // rollup handleExportDeals gives. .range() matters here: the org easily
  // has several thousand tasks, well past PostgREST's silent 1000-row cap.
  const handleExportTasksCsv = async () => {
    try {
      const { data: taskRows, error } = await supabase
        .from('tasks')
        .select('name, phase, status, assignee_name, assigned_to_contact, due_date, is_doc_request, notes, deals(name)')
        .eq('organization_id', organization?.id)
        .order('due_date', { ascending: true, nullsFirst: false })
        .range(0, 19999);
      if (error) throw error;

      const headers = ['Deal', 'Task', 'Phase', 'Status', 'Assignee', 'Assigned To (External)', 'Due Date', 'Doc Request', 'Notes'];
      const rows = (taskRows || []).map(t => [
        t.deals?.name, t.name, t.phase, t.status, t.assignee_name,
        t.assigned_to_contact, t.due_date, t.is_doc_request ? 'Yes' : '', t.notes,
      ]);
      const csv = [headers, ...rows].map(row => row.map(csvCell).join(',')).join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tasks-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast(`${rows.length} task(s) exported to CSV`);
    } catch (err) {
      showToast('Task export failed: ' + err.message, 'error');
    }
  };

  const handleExportPDF = async () => {
    // Calculate stats for the report
    const activeDeals = deals.filter(d => !['complete', 'dead', 'canceled'].includes(d.stage));
    const totalTasks = activeDeals.reduce((sum, d) => sum + (d.total_tasks || 0), 0);
    const completedTasks = activeDeals.reduce((sum, d) => sum + (d.completed_tasks || 0), 0);
    const requestedTasks = activeDeals.reduce((sum, d) => sum + (d.requested_tasks || 0), 0);
    const overdueDeals = activeDeals.filter(d => d.target_close_date && new Date(d.target_close_date) < new Date());
    const logoUrl = organization?.logo_url || LFG_LOGO_URL;
    
    // Fetch upcoming visits
    let upcomingVisits = [];
    try {
      const { data } = await supabase
        .from('deal_visits')
        .select('*, deals(name)')
        .eq('organization_id', organization?.id)
        .eq('status', 'scheduled')
        .gte('visit_date', new Date().toISOString().split('T')[0])
        .lte('visit_date', new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
        .order('visit_date', { ascending: true });
      upcomingVisits = data || [];
    } catch (err) {
      console.error('Failed to fetch visits for exec summary:', err);
    }
    
    // Build HTML for PDF
    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Executive Summary - ${new Date().toLocaleDateString()}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #333; max-width: 800px; margin: 0 auto; }
    .header { display: flex; align-items: center; gap: 20px; border-bottom: 2px solid #1A3665; padding-bottom: 15px; margin-bottom: 20px; }
    .header img { height: 120px; width: auto; }
    .header h1 { color: #1A3665; margin: 0; flex: 1; font-size: 24px; }
    h2 { color: #1A3665; margin-top: 30px; }
    .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin: 20px 0; }
    .stat-box { background: #f8f9fa; border-radius: 8px; padding: 15px; text-align: center; }
    .stat-value { font-size: 28px; font-weight: bold; color: #1A3665; }
    .stat-label { font-size: 12px; color: #707578; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th, td { padding: 10px; text-align: left; border-bottom: 1px solid #e5e7eb; }
    th { background: #f8f9fa; font-weight: 600; color: #1A3665; }
    .stage-badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; color: white; }
    .overdue { color: #dc2626; font-weight: 600; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #707578; text-align: center; }
    .footer img { height: 80px; margin-bottom: 10px; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <img src="${logoUrl}" alt="Logo" />
    <h1>Acquisition Portfolio Summary</h1>
  </div>
  <p style="color: #707578; margin-top: -10px;">Generated on ${new Date().toLocaleString()}</p>
  
  <div class="stats-grid">
    <div class="stat-box">
      <div class="stat-value">${categorizedDeals.active.length}</div>
      <div class="stat-label">Active Deals</div>
    </div>
    <div class="stat-box">
      <div class="stat-value">${categorizedDeals.onHold.length}</div>
      <div class="stat-label">On Hold</div>
    </div>
    <div class="stat-box">
      <div class="stat-value">${categorizedDeals.integration.length}</div>
      <div class="stat-label">Integration</div>
    </div>
    <div class="stat-box">
      <div class="stat-value">${requestedTasks}</div>
      <div class="stat-label">Docs Awaiting</div>
    </div>
    <div class="stat-box">
      <div class="stat-value">${categorizedDeals.complete.length}</div>
      <div class="stat-label">Complete</div>
    </div>
  </div>
  
  <h2>Pipeline by Stage</h2>
  <table>
    <tr><th>Stage</th><th>Count</th><th>% of Total</th></tr>
    ${PHASES.map(phase => {
      const count = deals.filter(d => d.stage === phase.id).length;
      const pct = deals.length ? Math.round((count / deals.length) * 100) : 0;
      return count > 0 ? `<tr><td><span class="stage-badge" style="background:${phase.color}">${phase.name}</span></td><td>${count}</td><td>${pct}%</td></tr>` : '';
    }).join('')}
  </table>
  
  <h2>Task Progress</h2>
  <table>
    <tr><th>Metric</th><th>Count</th></tr>
    <tr><td>Total Tasks (Active Deals)</td><td>${totalTasks}</td></tr>
    <tr><td>Completed</td><td>${completedTasks} (${totalTasks ? Math.round((completedTasks/totalTasks)*100) : 0}%)</td></tr>
    <tr><td>Pending</td><td>${totalTasks - completedTasks}</td></tr>
    <tr><td>Documents Requested</td><td>${requestedTasks}</td></tr>
  </table>
  
  <h2>Active Deals Summary</h2>
  <table>
    <tr><th>Deal</th><th>Stage</th><th>Progress</th><th>Target Close</th></tr>
    ${categorizedDeals.active.map(deal => {
      const phase = PHASES.find(p => p.id === deal.stage);
      const pct = deal.total_tasks ? Math.round((deal.completed_tasks / deal.total_tasks) * 100) : 0;
      const isOverdue = deal.target_close_date && new Date(deal.target_close_date) < new Date();
      return `<tr>
        <td>${deal.name}</td>
        <td><span class="stage-badge" style="background:${phase?.color || '#6b7280'}">${phase?.name || deal.stage}</span></td>
        <td>${pct}% (${deal.completed_tasks || 0}/${deal.total_tasks || 0})</td>
        <td class="${isOverdue ? 'overdue' : ''}">${deal.target_close_date ? new Date(deal.target_close_date).toLocaleDateString() : '-'}${isOverdue ? ' (OVERDUE)' : ''}</td>
      </tr>`;
    }).join('')}
  </table>
  
  ${overdueDeals.length > 0 ? `
  <h2 style="color: #ef4444;">Overdue Deals (${overdueDeals.length})</h2>
  <table>
    <tr><th>Deal</th><th>Target Close</th><th>Days Overdue</th></tr>
    ${overdueDeals.map(deal => {
      const daysOverdue = Math.ceil((new Date() - new Date(deal.target_close_date)) / (1000 * 60 * 60 * 24));
      return `<tr><td>${deal.name}</td><td>${new Date(deal.target_close_date).toLocaleDateString()}</td><td class="overdue">${daysOverdue} days</td></tr>`;
    }).join('')}
  </table>
  ` : ''}
  
  ${categorizedDeals.canceled.length > 0 ? `
  <h2 style="color: #ef4444;">Canceled Deals (${categorizedDeals.canceled.length})</h2>
  <table>
    <tr><th>Deal</th><th>Canceled Date</th><th>Reason</th></tr>
    ${categorizedDeals.canceled.map(deal => {
      return `<tr>
        <td>${deal.name}</td>
        <td>${deal.canceled_at ? new Date(deal.canceled_at).toLocaleDateString() : '-'}</td>
        <td>${deal.canceled_reason || 'No reason provided'}</td>
      </tr>`;
    }).join('')}
  </table>
  ` : ''}
  
  ${upcomingVisits.length > 0 ? `
  <h2>Upcoming Site Visits (Next 7 Days)</h2>
  <table>
    <tr><th>Date</th><th>Deal</th><th>Time</th><th>Scheduled By</th></tr>
    ${upcomingVisits.map(visit => `<tr>
      <td>${new Date(visit.visit_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
      <td>${visit.deals?.name || 'Unknown'}</td>
      <td>${visit.start_time?.substring(0, 5) || ''} - ${visit.end_time?.substring(0, 5) || ''}</td>
      <td>${visit.created_by_name || '-'}</td>
    </tr>`).join('')}
  </table>
  ` : ''}
  
  <div class="footer">
    <img src="${logoUrl}" alt="Landmark Funeral Group" />
    <p><strong>Landmark Funeral Group</strong></p>
    <p><em>Bridging Legacies. Serving Communities. Supporting Transitions.</em></p>
  </div>
</body>
</html>
    `;
    
    // Open in new window for printing/saving as PDF
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  const handleInviteMember = async () => {
    if (!newMember.name || !newMember.email) return;
    try {
      await inviteMember(newMember.email, newMember.name, newMember.role);
      setNewMember({ name: '', email: '', role: 'member' });
      showToast('Team member added');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  if (loading && deals.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: displayPrimary }} />
        <p className="text-gray-500 text-sm">Loading deals...</p>
      </div>
    );
  }

  return (
    <AppShell
      organization={organization}
      userProfile={userProfile}
      pc={pc}
      currentView="portfolio"
      setView={setView}
      setDarkMode={setDarkMode}
      darkMode={darkMode}
      signOut={signOut}
      isAdmin={isAdmin}
      isViewer={isViewer}
      isLimitedViewer={isLimitedViewer}
      isDealAdvisor={isDealAdvisor}
      onNewDeal={() => setShowModal('newDeal')}
      onFromFiles={() => { setShowModal('aiDeal'); setAiDealStep(null); setAiDealFiles([]); }}
      onExportCsv={handleExportDeals}
      onExportPdf={handleExportPDF}
      onExportFootprintCsv={handleExportFootprintCsv}
      onExportTasksCsv={handleExportTasksCsv}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-8 pb-12">
      {/* Page heading — deal count summary */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight" style={{
          background: 'linear-gradient(135deg, #0F172A 0%, #1A3665 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          letterSpacing: '-0.02em',
        }}>
          Portfolio
        </h1>
        <p className="text-sm mt-1" style={{ color: '#475569' }}>
          {anyFilterActive
            ? <span className="tabular-nums">{filteredDeals.length} of {deals.length} deals <span style={{ color: '#94A3B8' }}>· filtered</span></span>
            : <span className="tabular-nums">{deals.length} total deal{deals.length === 1 ? '' : 's'}</span>}
        </p>
      </div>

      {/* Summary Bar: 6 compact stat tiles (2 rows of 3) + 2 wider mini charts.
          Hidden for deal_advisor — pipeline-wide dashboard chrome that's
          meaningless (and mostly zeros) when scoped to 1-2 deals. */}
      {!isDealAdvisor && (
      <div className="flex flex-col lg:flex-row gap-3 mb-4 items-stretch">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 flex-[3]">
          <PortfolioStatTile label="Prospects"      value={categorizedDeals.prospects.length}                            accent="#64748B" icon={Sparkles} />
          <PortfolioStatTile label="Active Deals"   value={categorizedDeals.active.length}                               accent="#1A3665" icon={Briefcase} />
          <PortfolioStatTile label="On Hold"        value={categorizedDeals.onHold.length}                                accent="#A855F7" icon={Activity} />
          <PortfolioStatTile label="Integration"    value={categorizedDeals.integration.length}                          accent="#0EA5E9" icon={Building2} />
          <PortfolioStatTile label="Docs Requested" value={filteredDeals.reduce((sum, d) => sum + (d.requested_tasks || 0), 0)}  accent="#A16207" icon={FileText} />
          <PortfolioStatTile label="Complete"       value={categorizedDeals.complete.length}                             accent="#059669" icon={DollarSign} />
        </div>

        <div className="flex gap-3 flex-[2]">
        {/* Mini Chart: Deal Pipeline */}
        <div
          className="rounded-2xl px-4 py-3 flex items-center gap-3 flex-1 min-w-0"
          style={{
            background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFD 100%)',
            border: '1px solid #E5E9F0',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.02)',
          }}
        >
          <div className="relative w-12 h-12 flex-shrink-0">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              {(() => {
                const total = filteredDeals.length || 1;
                let cumulative = 0;
                return PHASES.map(phase => {
                  const count = filteredDeals.filter(d => d.stage === phase.id).length;
                  if (count === 0) return null;
                  const pct = (count / total) * 100;
                  const dashArray = `${pct} ${100 - pct}`;
                  const dashOffset = -cumulative;
                  cumulative += pct;
                  return (
                    <circle
                      key={phase.id}
                      cx="50" cy="50" r="40" fill="none"
                      stroke={phase.color} strokeWidth="18"
                      strokeDasharray={dashArray}
                      strokeDashoffset={dashOffset}
                      pathLength="100"
                    />
                  );
                });
              })()}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-sm font-semibold tabular-nums" style={{ color: '#0F172A' }}>{filteredDeals.length}</div>
            </div>
          </div>
          <div className="flex flex-col gap-0 min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#94A3B8' }}>Pipeline</div>
            {PHASES.map(phase => {
              const count = filteredDeals.filter(d => d.stage === phase.id).length;
              if (count === 0) return null;
              return (
                <div key={phase.id} className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: phase.color }} />
                  <span className="text-[10px] truncate tabular-nums" style={{ color: '#64748B' }}>{phase.name}: {count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mini Chart: Task Status */}
        <div
          className="rounded-2xl px-4 py-3 flex items-center gap-3 flex-1 min-w-0"
          style={{
            background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFD 100%)',
            border: '1px solid #E5E9F0',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.02)',
          }}
        >
          {(() => {
            const activeDeals = filteredDeals.filter(d => !['complete', 'dead', 'canceled'].includes(d.stage));
            const totalTasks = activeDeals.reduce((sum, d) => sum + (d.total_tasks || 0), 0);
            const completedTasks = activeDeals.reduce((sum, d) => sum + (d.completed_tasks || 0), 0);
            const pendingTasks = totalTasks - completedTasks;
            const overdueDeals = activeDeals.filter(d => d.target_close_date && new Date(d.target_close_date) < new Date());
            const overdueTasks = overdueDeals.reduce((sum, d) => sum + (d.total_tasks || 0) - (d.completed_tasks || 0), 0);
            const onTimeTasks = pendingTasks - overdueTasks;

            const taskStats = [
              { label: 'Done',     count: completedTasks,             color: '#10B981' },
              { label: 'On Track', count: Math.max(0, onTimeTasks),  color: '#94A3B8' },
              { label: 'Overdue',  count: overdueTasks,               color: '#EF4444' },
            ].filter(s => s.count > 0);

            const total = totalTasks || 1;
            let cumulative = 0;

            return (
              <>
                <div className="relative w-12 h-12 flex-shrink-0">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    {taskStats.map(stat => {
                      const pct = (stat.count / total) * 100;
                      const dashArray = `${pct} ${100 - pct}`;
                      const dashOffset = -cumulative;
                      cumulative += pct;
                      return (
                        <circle
                          key={stat.label}
                          cx="50" cy="50" r="40" fill="none"
                          stroke={stat.color} strokeWidth="18"
                          strokeDasharray={dashArray}
                          strokeDashoffset={dashOffset}
                          pathLength="100"
                        />
                      );
                    })}
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-sm font-semibold tabular-nums" style={{ color: '#0F172A' }}>{totalTasks}</div>
                  </div>
                </div>
                <div className="flex flex-col gap-0 min-w-0 flex-1">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#94A3B8' }}>Tasks</div>
                  {taskStats.map(stat => (
                    <div key={stat.label} className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: stat.color }} />
                      <span className="text-[10px] truncate tabular-nums" style={{ color: '#64748B' }}>{stat.label}: {stat.count}</span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
        </div>
      </div>
      )}

      {/* Capital Forecast Quick View — pipeline value + next-90-day cash outlay.
          Hidden from integration role (financial info not relevant to their work). */}
      {!isLimitedViewer && !isDealAdvisor && !isIntegration && (() => {
        // Scoped to deals actually being worked (diligence/offer-made/
        // under-loi) — Ray: "Pipeline value should only be Active."
        // Previously summed every non-dead deal, including prospects,
        // on-hold, and integration.
        const activeDealsWithPrice = filteredDeals.filter(d => ['diligence', 'offer-made', 'under-loi'].includes(d.stage) && d.purchase_price);
        const totalPipelineValue = activeDealsWithPrice.reduce((sum, d) => sum + Number(d.purchase_price || 0), 0);
        const next90 = activeDealsWithPrice.filter(d => {
          if (!d.target_close_date) return false;
          const days = Math.ceil((new Date(d.target_close_date) - new Date()) / 86400000);
          return days >= 0 && days <= 90;
        });
        const next90Value = next90.reduce((sum, d) => sum + Number(d.purchase_price || 0), 0);

        if (totalPipelineValue === 0) return null;

        return (
          <div
            className="mb-4 rounded-2xl px-4 sm:px-5 py-3 flex items-center justify-between flex-wrap gap-2 cursor-pointer transition-all hover:shadow-md group"
            style={{
              background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFD 100%)',
              border: '1px solid #E5E9F0',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.02)',
            }}
            onClick={() => setView('finance')}
          >
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                     style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', boxShadow: '0 2px 6px rgba(16, 185, 129, 0.25)' }}>
                  <DollarSign className="w-4 h-4 text-white" strokeWidth={2.2} />
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#94A3B8' }}>Active Pipeline Purchase Price Opportunity</div>
                  <div className="text-lg font-semibold tabular-nums leading-none mt-0.5" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
                    ${totalPipelineValue.toLocaleString()}
                  </div>
                </div>
              </div>
              {next90.length > 0 && (
                <div className="pl-6" style={{ borderLeft: '1px solid #E5E9F0' }}>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#94A3B8' }}>Next 90 days</div>
                  <div className="flex items-baseline gap-2 mt-0.5">
                    <span className="text-lg font-semibold tabular-nums leading-none" style={{ color: '#1A3665', letterSpacing: '-0.01em' }}>
                      ${next90Value.toLocaleString()}
                    </span>
                    <span className="text-[11px] tabular-nums" style={{ color: '#64748B' }}>
                      {next90.length} deal{next90.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <span className="text-xs font-medium transition-colors group-hover:text-slate-700" style={{ color: '#94A3B8' }}>
              View Forecast →
            </span>
          </div>
        );
      })()}

      {/* Filter bar — three compound dimensions: Territory, State, Stage.
          All combine with AND; within each row, chips combine with OR.
          Hidden entirely for deal_advisor — filtering by state/stage/
          priority is pointless chrome when scoped to 1-2 deals. */}
      {!isDealAdvisor && (availableStates.length > 0 || territories.length > 0 || deals.length > 0) && (
        <div
          className="mb-4 rounded-2xl px-4 py-3 space-y-2.5"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            border: '1px solid #E5E9F0',
            backdropFilter: 'blur(6px)',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
          }}
        >
          {/* Row 1 — Territories (named groupings) */}
          {!isLimitedViewer && !isDealAdvisor && (
            <div className="flex items-start gap-2 flex-wrap">
              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] mt-1.5" style={{ color: '#94A3B8', minWidth: '80px' }}>
                Territory
              </span>
              <div className="flex items-center gap-1.5 flex-wrap flex-1">
                {territories.map(t => {
                  const isActive = selectedTerritoryIds.has(t.id);
                  // Count deals that match this territory by EITHER the
                  // state list OR the drawn polygon.
                  const dealCount = deals.filter(d => {
                    const locs = dealLocations[d.id] || [];
                    for (const loc of locs) {
                      if (loc.state && (t.states || []).some(s => String(s).toUpperCase() === loc.state)) return true;
                      if (t.drawn_geometry && loc.lat != null && loc.lng != null && pointInPolygon(loc.lat, loc.lng, t.drawn_geometry)) return true;
                    }
                    return false;
                  }).length;
                  return (
                    <span
                      key={t.id}
                      className="inline-flex rounded-full overflow-hidden"
                      style={isActive ? {
                        background: `linear-gradient(135deg, ${t.color} 0%, ${t.color}CC 100%)`,
                        boxShadow: `0 1px 2px ${t.color}55`,
                      } : {
                        backgroundColor: 'white',
                        border: `1px solid ${t.color}40`,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleTerritory(t.id)}
                        title={t.description || `${(t.states || []).join(', ')}`}
                        className="h-7 inline-flex items-center gap-1.5 pl-2.5 pr-2 text-[11px] font-semibold transition-opacity hover:opacity-90"
                        style={{ color: isActive ? 'white' : '#475569' }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.9)' : t.color }} />
                        {t.name}
                        <span className="text-[10px] tabular-nums"
                              style={isActive ? { color: 'rgba(255,255,255,0.8)' } : { color: '#94A3B8' }}>
                          {dealCount}
                        </span>
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setEditingTerritory(t); }}
                          title={`Edit "${t.name}"`}
                          className="h-7 w-7 flex items-center justify-center transition-opacity hover:opacity-70"
                          style={{
                            borderLeft: isActive ? '1px solid rgba(255,255,255,0.25)' : `1px solid ${t.color}33`,
                            color: isActive ? 'rgba(255,255,255,0.85)' : '#64748B',
                          }}
                        >
                          <Pencil className="w-3 h-3" strokeWidth={2.2} />
                        </button>
                      )}
                    </span>
                  );
                })}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setEditingTerritory('new')}
                    className="h-7 inline-flex items-center gap-1 px-2.5 text-[11px] font-medium rounded-full transition-colors hover:bg-slate-100"
                    style={{
                      color: '#64748B',
                      border: '1px dashed #CBD5E1',
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    }}
                    title="Create a new named territory"
                  >
                    <Plus className="w-3 h-3" /> New territory
                  </button>
                )}
                {territories.length === 0 && !isAdmin && (
                  <span className="text-[11px] italic h-7 flex items-center" style={{ color: '#94A3B8' }}>
                    None yet — ask an admin to set up territories
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Row 2 — States (raw codes) */}
          {availableStates.length > 0 && (
            <div className="flex items-start gap-2 flex-wrap">
              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] mt-1.5" style={{ color: '#94A3B8', minWidth: '80px' }}>
                State
              </span>
              <div className="flex items-center gap-1.5 flex-wrap flex-1">
                {availableStates.map(s => {
                  const isActive = selectedStates.has(s.code);
                  return (
                    <button
                      key={s.code}
                      type="button"
                      onClick={() => toggleState(s.code)}
                      className="h-7 inline-flex items-center gap-1.5 px-2.5 text-[11px] font-semibold rounded-full transition-all"
                      style={isActive ? {
                        background: 'linear-gradient(135deg, #1A3665 0%, #2E4B7C 100%)',
                        color: 'white',
                        boxShadow: '0 1px 2px rgba(26, 54, 101, 0.35)',
                      } : {
                        // Pre-resolved per-mode values so the dark mode
                        // overrider's MutationObserver leaves them alone
                        // (which is what was causing the chip flicker).
                        backgroundColor: darkMode ? '#1c2230' : 'white',
                        color: darkMode ? '#e2e8f0' : '#475569',
                        border: darkMode ? '1px solid #2a3140' : '1px solid #E5E9F0',
                      }}
                    >
                      {s.code}
                      <span
                        className="text-[10px] tabular-nums"
                        style={isActive ? { color: 'rgba(255,255,255,0.8)' } : { color: '#94A3B8' }}
                      >
                        {s.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Row 3 — Stages (deal lifecycle) */}
          {deals.length > 0 && (
            <div className="flex items-start gap-2 flex-wrap">
              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] mt-1.5" style={{ color: '#94A3B8', minWidth: '80px' }}>
                Stage
              </span>
              <div className="flex items-center gap-1.5 flex-wrap flex-1">
                {PHASES.map(phase => {
                  const count = deals.filter(d => d.stage === phase.id).length;
                  if (count === 0) return null;
                  const isActive = selectedStages.has(phase.id);
                  return (
                    <button
                      key={phase.id}
                      type="button"
                      onClick={() => toggleStage(phase.id)}
                      className="h-7 inline-flex items-center gap-1.5 px-2.5 text-[11px] font-semibold rounded-full transition-all"
                      style={isActive ? {
                        background: `linear-gradient(135deg, ${phase.color} 0%, ${phase.color}CC 100%)`,
                        color: 'white',
                        boxShadow: `0 1px 2px ${phase.color}55`,
                      } : {
                        backgroundColor: darkMode ? '#1c2230' : 'white',
                        color: darkMode ? '#e2e8f0' : '#475569',
                        border: `1px solid ${phase.color}40`,
                      }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.9)' : phase.color }} />
                      {phase.name}
                      <span
                        className="text-[10px] tabular-nums"
                        style={isActive ? { color: 'rgba(255,255,255,0.8)' } : { color: '#94A3B8' }}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Row 4 — Priority (heat-map). Always rendered when at least one
              deal exists, even if no priorities are set yet — toggling the
              "No priority" chip is a fast way to find triage backlog. */}
          {deals.length > 0 && (() => {
            const PRIORITY_CHIPS = [
              { id: 'critical', label: 'Critical', color: '#DC2626' },
              { id: 'high',     label: 'High',     color: '#F97316' },
              { id: 'medium',   label: 'Medium',   color: '#EAB308' },
              { id: 'low',      label: 'Low',      color: '#CBD5E1' },
              { id: 'unset',    label: 'No priority', color: '#94A3B8' },
            ];
            return (
              <div className="flex items-start gap-2 flex-wrap">
                <span className="text-[10px] font-semibold uppercase tracking-[0.1em] mt-1.5" style={{ color: '#94A3B8', minWidth: '80px' }}>
                  Priority
                </span>
                <div className="flex items-center gap-1.5 flex-wrap flex-1">
                  {PRIORITY_CHIPS.map(pri => {
                    const count = deals.filter(d => (d.priority || 'unset') === pri.id).length;
                    if (count === 0) return null;
                    const isActive = selectedPriorities.has(pri.id);
                    return (
                      <button
                        key={pri.id}
                        type="button"
                        onClick={() => togglePriority(pri.id)}
                        className="h-7 inline-flex items-center gap-1.5 px-2.5 text-[11px] font-semibold rounded-full transition-all"
                        style={isActive ? {
                          background: `linear-gradient(135deg, ${pri.color} 0%, ${pri.color}CC 100%)`,
                          color: 'white',
                          boxShadow: `0 1px 2px ${pri.color}55`,
                        } : {
                          backgroundColor: darkMode ? '#1c2230' : 'white',
                          color: darkMode ? '#e2e8f0' : '#475569',
                          border: `1px solid ${pri.color}40`,
                        }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.9)' : pri.color }} />
                        {pri.label}
                        <span
                          className="text-[10px] tabular-nums"
                          style={isActive ? { color: 'rgba(255,255,255,0.8)' } : { color: '#94A3B8' }}
                        >
                          {count}
                        </span>
                      </button>
                    );
                  })}
                  {anyFilterActive && (
                    <button
                      type="button"
                      onClick={clearAllFilters}
                      className="h-7 inline-flex items-center gap-1 px-2.5 text-[11px] font-medium rounded-full transition-colors ml-auto hover:bg-slate-100"
                      style={{ color: '#64748B' }}
                    >
                      <X className="w-3 h-3" /> Clear all
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Territory create/edit modal */}
      {editingTerritory && (
        <TerritoryEditorModal
          territory={editingTerritory === 'new' ? null : editingTerritory}
          availableStates={availableStates}
          lfgLocations={lfgLocations}
          onClose={() => setEditingTerritory(null)}
          onSave={async (patch) => {
            try {
              if (editingTerritory === 'new') {
                await createTerritory(patch);
                showToast('Territory created');
              } else {
                await updateTerritory(editingTerritory.id, patch);
                showToast('Territory updated');
              }
              setEditingTerritory(null);
            } catch (err) { showToast(err.message, 'error'); }
          }}
          onDelete={editingTerritory !== 'new' ? async () => {
            if (!window.confirm(`Delete territory "${editingTerritory.name}"?`)) return;
            try {
              await deleteTerritory(editingTerritory.id);
              showToast('Territory deleted');
              setSelectedTerritoryIds(prev => { const n = new Set(prev); n.delete(editingTerritory.id); return n; });
              setEditingTerritory(null);
            } catch (err) { showToast(err.message, 'error'); }
          } : null}
          pc={pc}
        />
      )}

      {/* Phase T — Portfolio Radar agent briefing. Surfaces the latest
          AI-generated executive briefing at the top of the pipeline so
          Alfie/Will see signal before scrolling. Member+ can re-run.
          Hidden for deal_advisor — this is an org-wide briefing covering
          every deal/prospect, not scoped to deal_access, so it would leak
          cross-deal detail to a role meant to see only 1-2 specific deals. */}
      {!isDealAdvisor && (
        <BriefingsPanel
          organizationId={organization?.id}
          canGenerate={!isViewer}
          darkMode={darkMode}
          onSelectDeal={(id) => { setActiveDealId(id); setView('deal'); }}
        />
      )}

      {/* Phase R.2 — Sort dropdown + view-mode toggle + search. Sort
          applies inside every section so high-priority/closest/etc. deals
          float to the top of each bucket. View mode: cards (rich tiles)
          vs compact (1-row dense list). Search filters across every
          section by name / cluster / contact / email / city / state.
          Hidden for deal_advisor — none of this is useful with 1-2 deals. */}
      {!isDealAdvisor && (
      <div className="flex items-center gap-3 -mb-1 flex-wrap">
        {/* Search — full width on mobile, constrained on wider screens */}
        <div className="relative w-full sm:flex-1 sm:max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#94A3B8' }} />
          <input
            type="text"
            value={dealSearch}
            onChange={(e) => setDealSearch(e.target.value)}
            placeholder="Search deals by name, cluster, contact…"
            className="w-full h-9 pl-9 pr-9 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1"
            style={{
              backgroundColor: 'white',
              color: '#0F172A',
              border: '1px solid #E5E9F0',
            }}
          />
          {dealSearch && (
            <button
              onClick={() => setDealSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100"
              title="Clear search"
            >
              <X className="w-3.5 h-3.5" style={{ color: '#94A3B8' }} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex rounded-lg overflow-hidden" style={{ border: '1px solid #E5E9F0' }}>
          {[
            { id: 'compact', label: 'List' },
            { id: 'cards', label: 'Cards' },
          ].map(opt => {
            const active = viewMode === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setViewMode(opt.id)}
                className="h-8 px-3 text-[12px] font-semibold transition-colors"
                style={active
                  ? { backgroundColor: displayPrimary, color: 'white' }
                  : { backgroundColor: 'white', color: '#475569' }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em]" style={{ color: '#94A3B8' }}>
          Sort by
        </span>
        <select
          value={sortBy}
          onChange={(e) => handleSortChange(e.target.value)}
          className="h-8 pl-3 pr-8 text-[12px] font-semibold rounded-lg cursor-pointer transition-colors focus:outline-none"
          style={{
            backgroundColor: 'white',
            color: '#0F172A',
            border: '1px solid #E5E9F0',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394A3B8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2.5' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 8px center',
            backgroundSize: '11px',
          }}
        >
          <option value="priority">Priority (highest first)</option>
          <option value="updated">Last updated (newest first)</option>
          <option value="created">Date added (newest first)</option>
          <option value="target_close">Target close date (soonest first)</option>
          <option value="name">Name (A → Z)</option>
          <option value="cluster">Cluster (A → Z)</option>
          {!isIntegration && <option value="revenue">Revenue (largest first)</option>}
          <option value="distance">Distance to nearest LFG</option>
          <option value="custom">Custom (drag to reorder)</option>
        </select>
        </div>
      </div>
      )}

      {/* Column headers for the compact/list rows — deal rows show these
          six values with no labels otherwise. Card view already
          self-labels each field, so headers only apply here. Widths/
          responsive breakpoints mirror the row cells exactly below. */}
      {viewMode === 'compact' && !isDealAdvisor && !isIntegration && !isLimitedViewer && (
        <div className="hidden sm:flex items-center gap-3 px-4 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>
          <span className="flex-1">Deal</span>
          {/* Labels are left-aligned (not right-aligned like the values
              below) even though the box widths still match those value
              cells exactly. Right-aligning a short word like "Calls" in a
              fixed-width box leaves a lot of blank space before it, while
              a long label like "Purch. Price" leaves almost none — the
              visual gap between adjacent header words ends up wildly
              uneven (Calls/Purch. Price crowd together, Miles/Timing get
              a huge gap) even though every box is the exact same width
              as its column below. Left-aligning makes every gap exactly
              the flex `gap` value, with the box width still reserving
              the same footprint as the column it labels. */}
          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            <span className="w-16 whitespace-nowrap">Revenue</span>
            <span className="w-16 whitespace-nowrap">EBITDA</span>
            <span className="w-14 whitespace-nowrap">Calls</span>
            <span className="w-20 whitespace-nowrap">Purch. Price</span>
          </div>
          <span className="hidden md:inline-flex w-14 whitespace-nowrap">Miles</span>
          <span className="w-20 whitespace-nowrap">Timing</span>
        </div>
      )}

      {/* Deals — two-column card grid OR single-column compact list,
          depending on viewMode. Compact mode also force-spans full width. */}
      <div className={viewMode === 'compact' ? 'space-y-4' : 'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
        {sections.map(section => {
          if (section.deals.length === 0 && !section.emptyText) return null;

          // While searching, force-open every section so matches anywhere
          // (including in normally-collapsed Canceled/Complete) are visible.
          const isCollapsed = isSearching ? false : collapsedSections[section.key];
          const spanFull = viewMode === 'compact' || (section.key === 'active' && section.deals.length > 5);

          return (
            <div
              key={section.key}
              className={`rounded-2xl overflow-hidden relative ${spanFull ? 'col-span-2' : ''}`}
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #E5E9F0',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
              }}
            >
              {/* Accent strip — semantic color per section */}
              <div
                className="absolute top-0 left-0 right-0 h-[3px]"
                style={{ background: `linear-gradient(90deg, ${section.color} 0%, ${section.color}B3 30%, ${section.color}66 70%, transparent 100%)` }}
              />
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.key)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-[#FAFBFD] transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  {isCollapsed
                    ? <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />
                    : <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} />}
                  <h2 className="text-base font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
                    {section.label}
                  </h2>
                  <span
                    className="text-[10px] font-semibold tabular-nums px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: `${section.color}18`, color: section.color }}
                  >
                    {section.deals.length}
                  </span>
                </div>
              </button>

              {/* Deals List */}
              {!isCollapsed && (
                <div style={{ borderTop: '1px solid #F0F2F6' }}>
                  {section.deals.length === 0 ? (
                    <div className="px-6 py-10 text-center text-sm" style={{ color: '#94A3B8' }}>
                      {section.emptyText}
                    </div>
                  ) : viewMode === 'compact' ? (
                    // Phase R.2.1 — compact list rendering. One row per
                    // deal at ~36px so 20+ fit on screen. Click-through and
                    // delete still work the same as cards. Deleted-section
                    // rows render dimmed, no click-through, and a restore
                    // button in place of delete.
                    <DndContext
                      sensors={dndSensors}
                      collisionDetection={closestCenter}
                      onDragStart={({ active }) => setActiveDragId(active.id)}
                      onDragEnd={({ active, over }) => {
                        setActiveDragId(null);
                        if (!over || active.id === over.id || sortBy !== 'custom' || section.isDeleted) return;
                        const cur = getFullOrder(section.key, section.deals);
                        const oi = cur.indexOf(active.id), ni = cur.indexOf(over.id);
                        if (oi < 0 || ni < 0) return;
                        setCustomOrder(prev => ({ ...prev, [section.key]: arrayMove(cur, oi, ni) }));
                      }}
                      onDragCancel={() => setActiveDragId(null)}
                    >
                      <SortableContext items={section.deals.map(d => d.id)} strategy={verticalListSortingStrategy}>
                      <div>
                      {section.deals.map(deal => {
                        const phase = PHASES.find(p => p.id === deal.stage);
                        const isClosed = ['close', 'closed', 'complete'].includes(deal.stage) || phase?.is_terminal;
                        const isDeletedRow = !!section.isDeleted;
                        const canDelete = !isDeletedRow && !isViewer && !isDealAdvisor && (!isBoardMember || deal.created_by === userProfile?.id);
                        const canRestore = isDeletedRow && !isViewer && !isDealAdvisor && (!isBoardMember || deal.created_by === userProfile?.id);

                        const fmtMoney = (v) => {
                          if (!v) return '—';
                          const n = Number(v);
                          if (!Number.isFinite(n)) return '—';
                          if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
                          if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
                          return `$${n.toLocaleString()}`;
                        };

                        let dueText = null, dueColor = '#94A3B8';
                        if (!isClosed && deal.target_close_date) {
                          const days = Math.ceil((new Date(deal.target_close_date) - new Date()) / 86400000);
                          if (days < 0) { dueText = `${Math.abs(days)}d over`; dueColor = '#B91C1C'; }
                          else if (days <= 14) { dueText = `${days}d`; dueColor = '#C2410C'; }
                          else { dueText = new Date(deal.target_close_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
                        } else if (deal.target_close_date) {
                          dueText = new Date(deal.target_close_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        }

                        const PRI_DOT = {
                          critical: '#DC2626',
                          high:     '#F97316',
                          medium:   '#EAB308',
                          low:      '#CBD5E1',
                        };
                        const priDot = deal.priority ? PRI_DOT[deal.priority] : null;

                        // Pre-resolved per-mode colors. Anything that would
                        // otherwise hit the dark-mode overrider's lookup
                        // tables (#0F172A, #475569, #F0F2F6, #E5E9F0,
                        // #1A3665, #CBD5E1) gets flicker on every React
                        // re-render — set the dark value directly here so
                        // the MutationObserver leaves the row alone.
                        const rowText      = darkMode ? '#f1f5f9' : '#0F172A';
                        const rowMuted     = darkMode ? '#94a3b8' : '#475569';
                        const rowBorder    = darkMode ? '1px solid #252b38' : '1px solid #F0F2F6';
                        const rowDimEdge   = darkMode ? '1px solid #2a3140' : '1px solid #E5E9F0';
                        const rowProgressBg= darkMode ? '#252b38' : '#E5E9F0';
                        const distBright   = darkMode ? '#93b8e8' : '#1A3665';
                        const distDim      = darkMode ? '#475569' : '#CBD5E1';

                        return (
                          <SortableDealItem key={deal.id} id={deal.id} disabled={sortBy !== 'custom' || !!isDeletedRow}>
                          {({ listeners, attributes }) => (
                          <div
                            className={`group flex items-center gap-3 px-4 py-2 transition-colors text-[12px] ${isDeletedRow ? '' : 'cursor-pointer'}`}
                            style={{
                              borderBottom: rowBorder,
                              backgroundColor: isDeletedRow ? 'rgba(248, 250, 252, 0.7)' : undefined,
                              opacity: isDeletedRow ? 0.7 : 1,
                            }}
                            onClick={isDeletedRow ? undefined : () => { setActiveDealId(deal.id); setView('deal'); }}
                            onMouseEnter={(e) => { if (!isDeletedRow) e.currentTarget.style.backgroundColor = darkMode ? '#1c2230' : '#FAFBFD'; }}
                            onMouseLeave={(e) => { if (!isDeletedRow) e.currentTarget.style.backgroundColor = ''; }}
                          >
                            {/* Drag handle — only visible in custom sort mode */}
                            {sortBy === 'custom' && !isDeletedRow && (
                              <button
                                type="button"
                                {...listeners}
                                {...attributes}
                                onClick={e => e.stopPropagation()}
                                className="flex-shrink-0 touch-none cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-400 leading-none p-0"
                                title="Drag to reorder"
                              >
                                <GripVertical className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {/* Priority dot — single-pixel heat signal */}
                            <span
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: priDot || 'transparent', border: priDot ? 'none' : rowDimEdge }}
                              title={deal.priority ? `Priority: ${deal.priority}` : 'No priority set'}
                            />
                            {/* Name — flex grows, truncate. Displayed in title
                                case regardless of stored casing. Strikethrough
                                if deleted. */}
                            <span
                              className="font-semibold truncate min-w-0 flex-1"
                              style={{ color: rowText, textDecoration: isDeletedRow ? 'line-through' : 'none' }}
                              title={deal.name}
                            >
                              {titleCase(deal.name)}
                            </span>
                            {/* Deleted badge */}
                            {isDeletedRow && (
                              <span
                                className="text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0 inline-flex items-center gap-1"
                                style={{ backgroundColor: '#FEE2E2', color: '#B91C1C' }}
                                title={`Deleted ${deal.deleted_at ? new Date(deal.deleted_at).toLocaleDateString() : ''}`}
                              >
                                <Trash2 className="w-2.5 h-2.5" /> DELETED
                              </span>
                            )}
                            {/* Stage chip — hidden for deal_advisor (external, shouldn't see internal pipeline stage) */}
                            {!isDealAdvisor && (
                              <span
                                className="text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
                                style={{ backgroundColor: `${phase?.color || '#6b7280'}18`, color: phase?.color || '#6b7280' }}
                              >
                                {phase?.name || deal.stage}
                              </span>
                            )}
                            {/* Active-row status dot — only active-bucket deals carry staleTier */}
                            {!isDealAdvisor && deal.staleTier && (
                              <DealStatusDot deal={deal} delayCategories={delayCategories} />
                            )}
                            {/* Cluster tag — only if set */}
                            {deal.cluster && (
                              <span
                                className="text-[10px] font-medium px-1.5 py-0.5 rounded flex-shrink-0 truncate max-w-[90px]"
                                style={{ backgroundColor: 'rgba(124, 58, 237, 0.08)', color: '#5B21B6' }}
                                title={`Cluster: ${deal.cluster}`}
                              >
                                {deal.cluster}
                              </span>
                            )}
                            {/* Financials cluster (hidden for integration/limited/deal_advisor) */}
                            {!isIntegration && !isLimitedViewer && !isDealAdvisor && (
                              <div className="hidden md:flex items-center gap-3 tabular-nums flex-shrink-0" style={{ color: rowMuted }}>
                                <span className="w-16 text-right" title="Annual Revenue">{fmtMoney(deal.annual_revenue)}</span>
                                <span className="w-16 text-right" title="EBITDA">{fmtMoney(deal.ebitda)}</span>
                                <span className="w-14 text-right" title="Annual Calls">
                                  {deal.annual_calls ? Number(deal.annual_calls).toLocaleString() : '—'}
                                </span>
                                <span className="w-20 text-right" title="Purchase Price">{fmtMoney(deal.purchase_price)}</span>
                              </div>
                            )}
                            {/* Distance to nearest LFG */}
                            <span
                              className="hidden md:inline-flex items-center gap-0.5 w-14 justify-end tabular-nums flex-shrink-0"
                              style={{ color: dealDistances[deal.id] !== undefined ? distBright : distDim }}
                              title="Distance to nearest LFG location"
                            >
                              <MapPin className="w-3 h-3" />
                              {dealDistances[deal.id] !== undefined ? `${dealDistances[deal.id]}mi` : '—'}
                            </span>
                            {/* Target close */}
                            <span className="hidden sm:inline-block w-20 text-right tabular-nums flex-shrink-0" style={{ color: dueColor }}>
                              {dueText || '—'}
                            </span>
                            {/* Delete (visible on hover) — or Restore on
                                deleted rows. */}
                            {canDelete && (
                              <button
                                onClick={(e) => handleDeleteDeal(e, deal.id, deal.name)}
                                className="w-6 h-6 inline-flex items-center justify-center rounded transition-colors hover:bg-red-50 opacity-0 group-hover:opacity-100 flex-shrink-0"
                                style={{ color: '#EF4444' }}
                                title="Delete deal"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                            {canRestore && (
                              <button
                                onClick={(e) => handleRestoreDeal(e, deal.id, deal.name)}
                                className="h-6 inline-flex items-center gap-1 px-2 rounded text-[10px] font-semibold transition-colors hover:bg-emerald-100 flex-shrink-0"
                                style={{ color: '#047857', backgroundColor: '#ECFDF5', opacity: 1 }}
                                title="Restore this deal"
                              >
                                Restore
                              </button>
                            )}
                          </div>
                          )}
                          </SortableDealItem>
                        );
                      })}
                      </div>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <DndContext
                      sensors={dndSensors}
                      collisionDetection={closestCenter}
                      onDragStart={({ active }) => setActiveDragId(active.id)}
                      onDragEnd={({ active, over }) => {
                        setActiveDragId(null);
                        if (!over || active.id === over.id || sortBy !== 'custom' || section.isDeleted) return;
                        const cur = getFullOrder(section.key, section.deals);
                        const oi = cur.indexOf(active.id), ni = cur.indexOf(over.id);
                        if (oi < 0 || ni < 0) return;
                        setCustomOrder(prev => ({ ...prev, [section.key]: arrayMove(cur, oi, ni) }));
                      }}
                      onDragCancel={() => setActiveDragId(null)}
                    >
                    <SortableContext items={section.deals.map(d => d.id)} strategy={verticalListSortingStrategy}>
                    {section.deals.map(deal => {
                      const phase = PHASES.find(p => p.id === deal.stage);
                      // Closed deals stop showing pending-work nags ("X awaiting",
                      // overdue dates, days-inactive). The work either happened or
                      // it didn't — either way it's no longer actionable.
                      const isClosed = ['close', 'closed', 'complete'].includes(deal.stage) || phase?.is_terminal;

                      // Due date warning logic — suppressed for closed deals
                      let dueDateWarning = null;
                      if (!isClosed && deal.target_close_date) {
                        const daysUntilClose = Math.ceil((new Date(deal.target_close_date) - new Date()) / (1000 * 60 * 60 * 24));
                        if (daysUntilClose < 0) {
                          dueDateWarning = { text: `${Math.abs(daysUntilClose)}d overdue`, bg: '#FEE2E2', fg: '#B91C1C' };
                        } else if (daysUntilClose <= 7) {
                          dueDateWarning = { text: `${daysUntilClose}d left`, bg: '#FFEDD5', fg: '#C2410C' };
                        } else if (daysUntilClose <= 14) {
                          dueDateWarning = { text: `${daysUntilClose}d left`, bg: '#FEF3C7', fg: '#A16207' };
                        }
                      }

                      return (
                        <SortableDealItem key={deal.id} id={deal.id} disabled={sortBy !== 'custom'}>
                        {({ listeners, attributes }) => (
                        <div
                          className="px-5 py-3.5 transition-colors cursor-pointer hover:bg-[#FAFBFD] relative"
                          style={{ borderBottom: '1px solid #F0F2F6' }}
                          onClick={() => { setActiveDealId(deal.id); setView('deal'); }}
                        >
                          {sortBy === 'custom' && (
                            <button
                              type="button"
                              {...listeners}
                              {...attributes}
                              onClick={e => e.stopPropagation()}
                              className="absolute top-2 right-2 touch-none cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-400 p-0.5"
                              title="Drag to reorder"
                            >
                              <GripVertical className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <div className="flex justify-between items-start gap-3 mb-2">
                            <div className="flex items-center gap-2 flex-wrap min-w-0">
                              <Building2 className="w-4 h-4 flex-shrink-0" style={{ color: '#94A3B8' }} />
                              <span className="text-sm font-semibold" style={{ color: '#0F172A' }} title={deal.name}>{titleCase(deal.name)}</span>
                              {deal.priority && (() => {
                                const pri = {
                                  critical: { bg: '#FEE2E2', fg: '#B91C1C', label: 'CRITICAL' },
                                  high:     { bg: '#FFEDD5', fg: '#C2410C', label: 'HIGH' },
                                  medium:   { bg: '#FEF3C7', fg: '#A16207', label: 'MED' },
                                  low:      { bg: '#F1F5F9', fg: '#64748B', label: 'LOW' },
                                }[deal.priority];
                                if (!pri) return null;
                                return (
                                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                                        style={{ backgroundColor: pri.bg, color: pri.fg, letterSpacing: '0.04em' }}
                                        title={`Priority: ${deal.priority}`}>
                                    {pri.label}
                                  </span>
                                );
                              })()}
                              {!isDealAdvisor && (
                                <span
                                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                                  style={{ backgroundColor: `${phase?.color || '#6b7280'}18`, color: phase?.color || '#6b7280' }}
                                >
                                  {phase?.name || deal.stage}
                                </span>
                              )}
                              {!isClosed && deal.requested_tasks > 0 && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                                      style={{ backgroundColor: '#FEF3C7', color: '#A16207' }}>
                                  {deal.requested_tasks} awaiting
                                </span>
                              )}
                              {!isClosed && !isDealAdvisor && deal.staleTier && (
                                <DealStatusDot deal={deal} delayCategories={delayCategories} />
                              )}
                              {dueDateWarning && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                                      style={{ backgroundColor: dueDateWarning.bg, color: dueDateWarning.fg }}>
                                  {dueDateWarning.text}
                                </span>
                              )}
                              {dealDistances[deal.id] !== undefined && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                                      style={{ backgroundColor: 'rgba(26, 54, 101, 0.08)', color: '#1A3665' }}
                                      title="Distance to nearest LFG location">
                                  <MapPin className="w-3 h-3" /> {dealDistances[deal.id]} mi
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs tabular-nums" style={{ color: '#94A3B8' }}>
                                {deal.target_close_date ? new Date(deal.target_close_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'No target'}
                              </span>
                              {isDealAdvisor && setNotifyOnUpload && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try { await setNotifyOnUpload(deal.id, !deal.notify_on_upload); }
                                    catch (err) { showToast(err.message, 'error'); }
                                  }}
                                  className="h-7 inline-flex items-center gap-1 px-2 text-[11px] font-medium rounded-md transition-colors flex-shrink-0"
                                  style={deal.notify_on_upload
                                    ? { backgroundColor: 'rgba(26, 54, 101, 0.08)', color: '#1A3665', border: '1px solid rgba(26, 54, 101, 0.25)' }
                                    : { backgroundColor: 'white', color: '#64748B', border: '1px solid #E5E9F0' }}
                                  title={deal.notify_on_upload ? 'Notifying you on new uploads for this deal — click to turn off' : 'Get notified when files are uploaded to this deal'}
                                >
                                  <Bell className="w-3 h-3" fill={deal.notify_on_upload ? 'currentColor' : 'none'} />
                                  {deal.notify_on_upload ? 'Notifying' : 'Notify me'}
                                </button>
                              )}
                              {(() => {
                                // Members + admins delete any deal. Board members
                                // delete only deals they personally created. Other
                                // viewers can't delete at all — nor can deal_advisor,
                                // an external role that should never be able to
                                // delete the one deal it's scoped to.
                                const canDelete = !isViewer && !isDealAdvisor && (!isBoardMember || deal.created_by === userProfile?.id);
                                if (!canDelete) return null;
                                return (
                                  <button
                                    onClick={(e) => handleDeleteDeal(e, deal.id, deal.name)}
                                    className="w-7 h-7 inline-flex items-center justify-center rounded-md transition-colors hover:bg-red-50"
                                    style={{ color: '#EF4444' }}
                                    title="Delete deal"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                );
                              })()}
                            </div>
                          </div>
                          {/* Phase R.2 — At-a-glance stats: Revenue, EBITDA,
                              Calls, Purchase Price, Cluster. Suppressed for
                              integration role (financials hidden). Always
                              renders even when blank so the row stays
                              consistent across cards. */}
                          {!isIntegration && !isLimitedViewer && !isDealAdvisor && (() => {
                            const fmtMoney = (v) => {
                              if (!v) return '—';
                              const n = Number(v);
                              if (!Number.isFinite(n)) return '—';
                              if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
                              if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
                              return `$${n.toLocaleString()}`;
                            };
                            const fmtCalls = (v) => v ? Number(v).toLocaleString() : '—';
                            const stats = [
                              { label: 'Rev', value: fmtMoney(deal.annual_revenue) },
                              { label: 'EBITDA', value: fmtMoney(deal.ebitda) },
                              { label: 'Calls', value: fmtCalls(deal.annual_calls) },
                              { label: 'PP', value: fmtMoney(deal.purchase_price), title: 'Purchase Price' },
                              { label: 'Cluster', value: deal.cluster || '—' },
                            ];
                            return (
                              <div className="flex items-center gap-3 mb-2 text-[11px] flex-wrap" style={{ color: '#64748B' }}>
                                {stats.map((s, i) => (
                                  <span key={i} className="inline-flex items-center gap-1" title={s.title || s.label}>
                                    <span className="font-semibold uppercase tracking-wider" style={{ color: '#94A3B8', fontSize: '9px', letterSpacing: '0.08em' }}>
                                      {s.label}
                                    </span>
                                    <span className="tabular-nums" style={{ color: s.value === '—' ? '#CBD5E1' : '#334155', fontWeight: s.value === '—' ? 400 : 600 }}>
                                      {s.value}
                                    </span>
                                  </span>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                        )}
                        </SortableDealItem>
                      );
                    })}
                    </SortableContext>
                    </DndContext>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* New Deal Modal */}
      {showModal === 'newDeal' && (
        <Modal onClose={() => setShowModal(null)} title="New Deal" pc={pc}>
          <input value={newDealName} onChange={e => setNewDealName(e.target.value)} placeholder="Funeral Home Name" className="w-full border rounded px-3 py-2 mb-4" autoFocus />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowModal(null)} className="px-4 py-2 border rounded hover:bg-gray-50 transition-colors">Cancel</button>
            <button onClick={handleCreateDeal} className="px-4 py-2 text-white rounded hover:opacity-90 transition-opacity" style={{ backgroundColor: pc }}>Create</button>
          </div>
        </Modal>
      )}

      {/* AI Deal from Files Modal */}
      {showModal === 'aiDeal' && (
        <Modal onClose={() => { if (aiDealStep !== 'creating') { setShowModal(null); setAiDealStep(null); setAiDealFiles([]); } }} title="New Deal from Files" pc={pc} maxWidth="max-w-lg">
          {/* Step 0: File staging — accepts multiple batches before
              kicking off AI extraction. User can drop from desktop,
              browse, then paste from Outlook, etc. Files accumulate in
              aiDealFiles; dedupe by name+size; per-row remove; explicit
              Continue button to proceed. */}
          {!aiDealStep && (
            <div>
              <p className="text-sm text-gray-600 mb-4">Stage files from any source — drag from your desktop, paste from Outlook, browse — then click Continue. AI will extract the business name, contact info, and categorize all the documents at once.</p>
              <div
                onClick={() => aiDealFileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); e.stopPropagation(); setAiDealDragActive(true); }}
                onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setAiDealDragActive(true); }}
                onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setAiDealDragActive(false); }}
                onDrop={e => { e.preventDefault(); e.stopPropagation(); setAiDealDragActive(false); if (e.dataTransfer.files?.length) handleAiDealFilesAdd(e.dataTransfer.files); }}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                  aiDealDragActive ? 'border-purple-400 bg-purple-50 scale-[1.01]' : 'hover:border-purple-400 hover:bg-purple-50/50'
                }`}
              >
                <Upload className={`w-8 h-8 mx-auto mb-2 ${aiDealDragActive ? 'text-purple-500' : 'text-gray-400'}`} />
                <p className="font-medium text-sm text-gray-700">
                  {aiDealDragActive
                    ? 'Drop files to add'
                    : aiDealFiles.length > 0 ? 'Drop more files or click to add' : 'Drop files here or click to browse'}
                </p>
                <p className="text-xs text-gray-400 mt-1">PDFs, spreadsheets, documents, images. Add as many batches as you need.</p>
                <input
                  ref={aiDealFileRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.txt,.ppt,.pptx"
                  onChange={e => { handleAiDealFilesAdd(e.target.files); e.target.value = ''; }}
                />
              </div>

              {/* Staged file list — only renders once at least one batch
                  has been added. Per-row remove + total summary footer. */}
              {aiDealFiles.length > 0 && (
                <div className="mt-3 border rounded-lg overflow-hidden" style={{ borderColor: '#E5E9F0' }}>
                  <div
                    className="px-3 py-2 flex items-center justify-between text-[11px] font-semibold uppercase"
                    style={{ backgroundColor: '#FAFBFD', color: '#64748B', letterSpacing: '0.06em', borderBottom: '1px solid #F0F2F6' }}
                  >
                    <span>{aiDealFiles.length} file{aiDealFiles.length === 1 ? '' : 's'} staged</span>
                    <button
                      type="button"
                      onClick={() => setAiDealFiles([])}
                      className="text-[10px] font-medium normal-case hover:underline"
                      style={{ color: '#94A3B8', letterSpacing: 'normal' }}
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="max-h-[180px] overflow-y-auto">
                    {aiDealFiles.map((f, i) => {
                      const ext = (f.name.split('.').pop() || '').toLowerCase();
                      const sizeLabel = f.size > 1048576
                        ? (f.size / 1048576).toFixed(1) + ' MB'
                        : Math.round(f.size / 1024) + ' KB';
                      return (
                        <div key={`${f.name}-${i}`} className="flex items-center gap-2 px-3 py-2 text-[12px]" style={{ borderBottom: '1px solid #F0F2F6' }}>
                          <span
                            className="w-7 h-6 rounded text-[9px] font-bold uppercase inline-flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: '#EFF4FB', color: '#1A3665' }}
                          >
                            {ext.slice(0, 4) || '—'}
                          </span>
                          <span className="flex-1 truncate" style={{ color: '#0F172A' }} title={f.name}>{f.name}</span>
                          <span className="text-[10px] tabular-nums flex-shrink-0" style={{ color: '#94A3B8' }}>{sizeLabel}</span>
                          <button
                            type="button"
                            onClick={() => handleAiDealFileRemove(i)}
                            className="p-1 rounded hover:bg-red-50 flex-shrink-0"
                            title="Remove from staging"
                          >
                            <X className="w-3.5 h-3.5" style={{ color: '#EF4444' }} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action row — Continue is enabled once at least one file is staged. */}
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setShowModal(null); setAiDealStep(null); setAiDealFiles([]); }}
                  className="px-4 py-2 border rounded text-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAiDealStartExtraction}
                  disabled={aiDealFiles.length === 0}
                  className="px-4 py-2 rounded text-sm font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)' }}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Continue with {aiDealFiles.length || 0} file{aiDealFiles.length === 1 ? '' : 's'}
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Extracting */}
          {aiDealStep === 'extracting' && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-purple-500" />
              <p className="font-medium text-gray-700">Reading {aiDealFiles.length} file{aiDealFiles.length > 1 ? 's' : ''}...</p>
              <p className="text-sm text-gray-400 mt-1">AI is extracting business information</p>
            </div>
          )}

          {/* Step 2: Confirm extracted info */}
          {aiDealStep === 'confirm' && (
            <div>
              {aiDealInfo.notes && (
                <div className="text-xs text-purple-600 bg-purple-50 rounded px-3 py-2 mb-3 flex items-start gap-2">
                  <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  {aiDealInfo.notes}
                </div>
              )}
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-600">Business Name *</label>
                  <input value={aiDealInfo.business_name} onChange={e => setAiDealInfo(p => ({ ...p, business_name: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm mt-0.5" autoFocus />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600">Contact Name</label>
                    <input value={aiDealInfo.primary_contact} onChange={e => setAiDealInfo(p => ({ ...p, primary_contact: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm mt-0.5" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">Phone</label>
                    <input value={aiDealInfo.primary_phone} onChange={e => setAiDealInfo(p => ({ ...p, primary_phone: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm mt-0.5" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-medium text-gray-600">Email</label>
                    <input value={aiDealInfo.primary_email} onChange={e => setAiDealInfo(p => ({ ...p, primary_email: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm mt-0.5" />
                  </div>
                </div>
              </div>

              {/* Locations */}
              <div className="mt-3 pt-3 border-t">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-medium text-gray-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Locations ({aiDealInfo.locations.length})
                  </div>
                  <button
                    type="button"
                    onClick={() => setAiDealInfo(p => ({ ...p, locations: [...(p.locations || []), { name: '', address: '', city: '', state: '', zip: '' }] }))}
                    className="text-xs px-2 py-0.5 rounded border hover:bg-gray-50 flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>
                <div className="space-y-2">
                  {(aiDealInfo.locations || []).map((loc, i) => (
                    <div key={i} className="border rounded p-2 bg-gray-50/50">
                      <div className="flex items-center gap-2 mb-1">
                        <input
                          value={loc.name || ''}
                          onChange={e => setAiDealInfo(p => {
                            const next = [...p.locations]; next[i] = { ...next[i], name: e.target.value }; return { ...p, locations: next };
                          })}
                          placeholder="Location name"
                          className="flex-1 border rounded px-2 py-1 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setAiDealInfo(p => ({ ...p, locations: p.locations.filter((_, idx) => idx !== i) }))}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-6 gap-1.5">
                        <input
                          value={loc.address || ''}
                          onChange={e => setAiDealInfo(p => {
                            const next = [...p.locations]; next[i] = { ...next[i], address: e.target.value }; return { ...p, locations: next };
                          })}
                          placeholder="Street address"
                          className="col-span-6 border rounded px-2 py-1 text-xs"
                        />
                        <input
                          value={loc.city || ''}
                          onChange={e => setAiDealInfo(p => {
                            const next = [...p.locations]; next[i] = { ...next[i], city: e.target.value }; return { ...p, locations: next };
                          })}
                          placeholder="City"
                          className="col-span-3 border rounded px-2 py-1 text-xs"
                        />
                        <input
                          value={loc.state || ''}
                          onChange={e => setAiDealInfo(p => {
                            const next = [...p.locations]; next[i] = { ...next[i], state: e.target.value.toUpperCase() }; return { ...p, locations: next };
                          })}
                          placeholder="ST"
                          maxLength={2}
                          className="col-span-1 border rounded px-2 py-1 text-xs"
                        />
                        <input
                          value={loc.zip || ''}
                          onChange={e => setAiDealInfo(p => {
                            const next = [...p.locations]; next[i] = { ...next[i], zip: e.target.value }; return { ...p, locations: next };
                          })}
                          placeholder="ZIP"
                          className="col-span-2 border rounded px-2 py-1 text-xs"
                        />
                      </div>
                    </div>
                  ))}
                  {aiDealInfo.locations.length === 0 && (
                    <div className="text-xs text-gray-400 italic">No locations detected. Click Add to create one.</div>
                  )}
                </div>
              </div>

              {/* Broker / Consultant */}
              {(aiDealInfo.broker_name || aiDealInfo.broker_firm) && (
                <div className="mt-3 pt-3 border-t">
                  <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                    <Briefcase className="w-3 h-3" /> Broker / Consultant
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600">Name</label>
                      <input value={aiDealInfo.broker_name} onChange={e => setAiDealInfo(p => ({ ...p, broker_name: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm mt-0.5" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600">Firm</label>
                      <input value={aiDealInfo.broker_firm} onChange={e => setAiDealInfo(p => ({ ...p, broker_firm: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm mt-0.5" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600">Email</label>
                      <input value={aiDealInfo.broker_email} onChange={e => setAiDealInfo(p => ({ ...p, broker_email: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm mt-0.5" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600">Phone</label>
                      <input value={aiDealInfo.broker_phone} onChange={e => setAiDealInfo(p => ({ ...p, broker_phone: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm mt-0.5" />
                    </div>
                  </div>
                </div>
              )}

              <div className="text-xs text-gray-400 mt-3 mb-4">
                {aiDealFiles.length} file{aiDealFiles.length > 1 ? 's' : ''} will be uploaded and processed after creation: {aiDealFiles.map(f => f.name).join(', ')}
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => { setShowModal(null); setAiDealStep(null); setAiDealFiles([]); }} className="px-4 py-2 border rounded hover:bg-gray-50 transition-colors">Cancel</button>
                <button onClick={handleAiDealConfirm} disabled={!aiDealInfo.business_name.trim()} className="px-4 py-2 text-white rounded hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2" style={{ backgroundColor: pc }}>
                  <Sparkles className="w-4 h-4" /> Create Deal & Process Files
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Creating */}
          {aiDealStep === 'creating' && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-purple-500" />
              <p className="font-medium text-gray-700">Creating deal & uploading files...</p>
              <p className="text-sm text-gray-400 mt-1">{aiDealFiles.length} files will be categorized by AI in the background</p>
            </div>
          )}
        </Modal>
      )}
      </div>{/* end inner max-w-7xl container */}
    </AppShell>
  );
}

// ============================================================================
// TIMELINE VIEW WRAPPER (loads deals for timeline)
// ============================================================================
function TimelineViewWrapper({ organization, pc, sc, setView, setActiveDealId, signOut }) {
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(pc, sc);
  const { deals, loading } = useDeals(organization?.id);
  const { stages } = useDealStages(organization?.id);

  if (loading && deals.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: displayPrimary }} />
        <p className="text-gray-500 text-sm">Loading timeline...</p>
      </div>
    );
  }

  return (
    <TimelineView
      deals={deals}
      onSelectDeal={(dealId) => {
        setActiveDealId(dealId);
        setView('deal');
      }}
      onBack={() => setView('portfolio')}
      pc={pc}
      sc={sc}
      stages={stages}
      signOut={signOut}
    />
  );
}

// ============================================================================
// MAP VIEW WRAPPER
// ============================================================================
function MapViewWrapper({ organization, pc, sc, setView, setActiveDealId, signOut }) {
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(pc, sc);
  const { deals, loading } = useDeals(organization?.id);
  const { stages } = useDealStages(organization?.id);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: displayPrimary }} />
        <p className="text-gray-500 text-sm">Loading map...</p>
      </div>
    );
  }

  return (
    <MapView
      deals={deals}
      stages={stages}
      organizationId={organization?.id}
      onSelectDeal={(dealId) => {
        setActiveDealId(dealId);
        setView('deal');
      }}
      onBack={() => setView('portfolio')}
      pc={pc}
      sc={sc}
      signOut={signOut}
    />
  );
}

// ============================================================================
// MODAL COMPONENT
// ============================================================================
function Modal({ children, onClose, title, maxWidth = 'max-w-md', pc = '#1A3665' }) {
  const { displayPrimary } = useDisplayColors(pc, null);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg w-full ${maxWidth} max-h-[90vh] flex flex-col`}>
        <div className="p-4 border-b flex justify-between items-center flex-shrink-0">
          <h3 className="font-semibold text-lg" style={{ color: displayPrimary }}>{title}</h3>
          <button onClick={onClose} className="hover:bg-gray-100 p-1 rounded transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

// Export constants for use in other components
// Dev/preview banner — visible on anything that isn't the prod hostname.
// Auto-hides on ma.landmarkfuneralgroup.com, so merging to main needs no cleanup.
function DevBanner() {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  if (host === 'ma.landmarkfuneralgroup.com') return null;
  const label = host === 'localhost' || host === '127.0.0.1' ? 'LOCAL DEV' : 'DEVELOPMENT';
  return (
    <>
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: 'repeating-linear-gradient(45deg, #ef4444, #ef4444 10px, #b91c1c 10px, #b91c1c 20px)',
          color: 'white', fontWeight: 'bold', fontSize: '12px', letterSpacing: '0.1em',
          textAlign: 'center', padding: '4px 12px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          textShadow: '0 1px 2px rgba(0,0,0,0.4)',
          pointerEvents: 'none',
        }}
      >
        ⚠ {label} · {host} · NOT PRODUCTION ⚠
      </div>
      {/* Push page content down so nothing hides under the fixed banner */}
      <div style={{ height: '26px' }} aria-hidden="true" />
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <DevBanner />
      <AppInner />
    </ThemeProvider>
  );
}

export { LFG_COLORS, LFG_LOGO_URL, DEFAULT_PHASES as PHASES };
